import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFeathers } from '../../app/util';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import { DataDetailToolbar, CSVDropzone, StepTracker, PartyTag } from '../../components';
import { Tile, CellRenderer } from '../../components';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { CreateCandidateList, CandidateList, PatchCandidateList, LabeledColumn, ColumnLabel, LabeledData, MatchedData, GroupedData, CandidateListCandidate, DataOperation } from '../../app/feathers/candidate-lists/CandidateList';
import { Office } from '../../app/feathers/offices/Office';
import { District } from '../../app/feathers/districts/District';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import {
  getCandidateListColumnMappingOptions,
  updateRawCellValue
} from '../../util/column-mapping-utils';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import Checkbox from '@mui/material/Checkbox';
import RaceCell from './RaceCell';
import OfficialsCell from './OfficialsCell';
import DataOperationsTable, { ChangeOfficialMatchPayload } from './DataOperationsTable';
import { ScoutingStepContent } from './ScoutingStepContent';
import VerifyDataElapsedTime from './VerifyDataElapsedTime';
import VerifyDataRow, { VerifyDataRowHandlers } from './VerifyDataRow';
import {
  filterVerifyDataRows,
  getVerifyDataPage
} from './verify-data-utils';
import { Election } from '../../app/feathers/elections/Election';
import { Official } from '../../app/feathers/officials/Official';
import { Race } from '../../app/feathers/races/Race';
import { StateElectionConfiguration } from '../../app/feathers/state-election-configurations/StateElectionConfiguration';
import { Party } from '../../app/feathers/parties/Party';
import {
  CandidateListRematchMode,
  CANDIDATE_LIST_STEPS,
  getCandidateListRematchCommand,
  getDisplayedStatus,
  getDisplayedStepName,
  getProgressStepIndex,
  hasUnmatchedMatchingData,
  isStepAvailable,
  markRaceMatchAsUser
} from './workflow';

type GroupedDataWithLabels = GroupedData & LabeledData & {
  officeInfo?: Office;
  districtInfo?: District;
  groupedDataIndex: number;
};

function indexFirstBy<T>(items: T[] | null | undefined, getKey: (item: T) => string | undefined) {
  const index = new Map<string, T>();
  (items || []).forEach(item => {
    const key = getKey(item);
    if (key && !index.has(key)) {
      index.set(key, item);
    }
  });
  return index;
}

const getGroupPartyKey = (groupLabel: string, party?: string) => (
  `${groupLabel}|${party || 'no-party'}`
);

// Function to get column mapping options based on election type
const getColumnMappingOptions = (
  election: any,
  allowedPartyKeys: string[] = [],
  partyOptions: Party[] = []
) => {
  const options = getCandidateListColumnMappingOptions(allowedPartyKeys, partyOptions);
  
  if (election?.partisan === true) {
    // For primary elections: require raceParty, exclude party
    return options.filter(option => option.value !== 'party');
  } else {
    return options.filter(option => option.value !== 'raceParty');
  }
};

// Function to get required fields based on election type
const getRequiredFields = (election: any, allowedPartyKeys: string[] = [], partyOptions: Party[] = []) => {
  return getColumnMappingOptions(election, allowedPartyKeys, partyOptions).filter(option => option.required);
};

// Function to check if a row has all empty values
const isRowEmpty = (row: Record<string, any>): boolean => {
  return Object.values(row).every(value => {
    // Check if value is null, undefined, empty string, or whitespace-only
    return value === null || 
           value === undefined || 
           (typeof value === 'string' && value.trim() === '');
  });
};

const getErrorMessage = (error: any, fallbackMessage: string) => {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;
  if (error?.errors && Object.values(error.errors).length > 0) {
    return Object.values(error.errors).join(' ');
  }
  return error?.message || error?.error?.message || fallbackMessage;
};

const columnLabels = new Set<ColumnLabel>([
  'withdrawn',
  'groupLabel',
  'ballotOrder',
  'maxChoices',
  'raceParty',
  'name',
  'party',
  'incumbent',
  'writeIn',
  'address',
  'email',
  'phone',
  'secondaryEmail',
  'secondaryPhone',
  'website',
  'occupation',
  'officeLabel',
  'districtLabel'
]);

const getColumnLabel = (value?: string): ColumnLabel | undefined => (
  value && columnLabels.has(value as ColumnLabel) ? value as ColumnLabel : undefined
);

const UploadCandidateList = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const history = useHistory();
  const {
    key: electionKey,
    id: candidateListId,
    stepName: activeStepName
  } = useParams<{ key: string; id: string; stepName?: string }>();
  const user = useSelector((state: any) => state.user);
  const electionDataPreloaded = useSelector((state: any) => state?.elections?.byKey[electionKey]);
  const [election, setElection] = useState<Election | null>(electionDataPreloaded);
  const [stateElectionConfig, setStateElectionConfig] = useState<StateElectionConfiguration | null>(null);
  const [partyOptions, setPartyOptions] = useState<Party[]>([]);

  // Step 1: Upload Data
  const [rawCsvData, setRawCsvData] = useState<any[] | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[] | null>(null);
  const [source, setSource] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Step 2: mapping columns, clean and validate data
  const [candidateList, setCandidateList] = useState<CandidateList | null>(null);
  const displayedStepName = getDisplayedStepName(
    activeStepName,
    candidateList?.status,
    candidateListId === 'new'
  );
  const displayedStepIndex = CANDIDATE_LIST_STEPS.findIndex(
    step => step.routeName === displayedStepName
  );
  const displayedStatus = getDisplayedStatus(displayedStepName, candidateList?.status);
  const [labeledColumns, setLabeledColumns] = useState<LabeledColumn[]>([]);
  const [cleanedData, setCleanedData] = useState<{
    rawData: Record<string, string>;
    cleanedData: Record<ColumnLabel, any>;
    validationErrors: Record<ColumnLabel, string | null>;
  }[] | null>(null);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isConfirmingMapping, setIsConfirmingMapping] = useState(false);
  const [mappingConfirmationError, setMappingConfirmationError] = useState<string | null>(null);
  const [pendingCellEdit, setPendingCellEdit] = useState<{
    rowIndex: number;
    columnName: string;
    columnLabel: ColumnLabel;
    value: string;
  } | null>(null);
  const [isSavingCellEdit, setIsSavingCellEdit] = useState(false);
  const [cellEditError, setCellEditError] = useState<string | null>(null);

  // Step 3: Label each race with office and district
  const [groupedData, setGroupedData] = useState<any[] | null>(null);
  const [labeledData, setLabeledData] = useState<LabeledData[] | null>(null);
  const [officeData, setOfficeData] = useState<Office[] | null>(null);
  const [districtData, setDistrictData] = useState<District[] | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedGroupedDataRows, setSelectedGroupedDataRows] = useState<Set<number>>(new Set());
  const [isDeletingGroupedDataRows, setIsDeletingGroupedDataRows] = useState(false);
  const [groupedDataDeletionError, setGroupedDataDeletionError] = useState<string | null>(null);
  const [showOnlyNeedsAttention, setShowOnlyNeedsAttention] = useState(false);
  const [verifyDataSearch, setVerifyDataSearch] = useState('');
  const [verifyDataPage, setVerifyDataPage] = useState(1);
  const verifyDataSearchInputRef = useRef<HTMLInputElement>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showEntityRematchModal, setShowEntityRematchModal] = useState(false);
  const [isRematchingEntities, setIsRematchingEntities] = useState(false);
  const [entityRematchError, setEntityRematchError] = useState<string | null>(null);

  // Step 4: Match each race with an existing official and/or race
  const [matchedData, setMatchedData] = useState<MatchedData[] | null>(null);
  const [officialData, setOfficialData] = useState<Official[] | null>(null);
  const [raceData, setRaceData] = useState<Race[] | null>(null);
  const [isRetryingMatching, setIsRetryingMatching] = useState(false);
  const [matchingRetryError, setMatchingRetryError] = useState<string | null>(null);
  const [isCancellingMatching, setIsCancellingMatching] = useState(false);
  const [matchingCancellationError, setMatchingCancellationError] = useState<string | null>(null);
  const [deletingMatchRow, setDeletingMatchRow] = useState<string | null>(null);
  const [matchRowDeletionError, setMatchRowDeletionError] = useState<string | null>(null);
  const [pendingMatchRowDeletion, setPendingMatchRowDeletion] = useState<
    | { type: 'race'; groupIndex: number; label: string }
    | { type: 'candidate'; groupIndex: number; candidateIndex: number; label: string }
    | null
  >(null);
  
  // Refs to track fetched officials and races to prevent infinite loops
  const fetchedOfficialKeysRef = useRef<Set<string>>(new Set());
  const fetchedRaceIdsRef = useRef<Set<string>>(new Set());

  // Step 6: Create entities
  const [dataOperations, setDataOperations] = useState<DataOperation[] | null>(null);
  const [isCancellingCreate, setIsCancellingCreate] = useState(false);
  const [isRegeneratingDataOperations, setIsRegeneratingDataOperations] = useState(false);
  const [regenerateDataOperationsError, setRegenerateDataOperationsError] = useState<string | null>(null);
  const isCreatingDataOperations = candidateList?.processInProgress?.stage === 'creating';
  const hasAttemptedDataOperations = dataOperations?.some(op => ['completed', 'failed', 'corrected'].includes(op.status)) ?? false;
  const hasFinishedDataOperations = dataOperations
    ?.some(operation => ['failed', 'completed'].includes(operation.status)) ?? false;
  const shouldDefaultToOperationsTab = isCreatingDataOperations || hasFinishedDataOperations;
  const hasRetryableDataOperations = dataOperations?.some(op => ['failed', 'not-started'].includes(op.status)) ?? false;
  const canRetryDataOperations = !isCreatingDataOperations && hasAttemptedDataOperations && hasRetryableDataOperations;
  const canChangeOfficialMatches = Boolean(candidateList) &&
    !isCreatingDataOperations &&
    (dataOperations || []).every(operation => ['not-started', 'no-op'].includes(operation.status));
  const dataOperationsErrorAlert = candidateList?.dataOperationsError ? (
    <Box
      sx={{
        p: 2,
        backgroundColor: '#ffebee',
        border: '1px solid #ffcdd2',
        borderRadius: '8px',
        marginBottom: '16px'
      }}
    >
      <Typography variant="subtitle2" sx={{ color: '#d32f2f', fontWeight: 600, marginBottom: '4px' }}>
        Data operation processing failed
      </Typography>
      <Typography variant="body2" sx={{ color: '#b71c1c' }}>
        {candidateList.dataOperationsError}
      </Typography>
    </Box>
  ) : null;

  const primaryPartyKeys = useMemo(() => {
    const supportedPartiesRaw = stateElectionConfig?.supportedPartiesForPrimary || [];
    return supportedPartiesRaw
      .map((party) => (typeof party === 'string' ? party : party?.key))
      .filter((party): party is string => Boolean(party))
      .map((party) => party.toUpperCase());
  }, [stateElectionConfig?.supportedPartiesForPrimary]);
  const allPartyKeys = useMemo(() => {
    return partyOptions
      .map((party) => party.key)
      .filter((party): party is string => Boolean(party))
      .map((party) => party.toUpperCase());
  }, [partyOptions]);
  const allowedPartyKeys = election?.partisan === true ? primaryPartyKeys : allPartyKeys;
  const allowedPartyOptions = useMemo(() => {
    const allowedPartyKeySet = new Set(allowedPartyKeys);
    return partyOptions.filter((party) => allowedPartyKeySet.has(party.key.toUpperCase()));
  }, [allowedPartyKeys, partyOptions]);

  // Get current column mapping options and required fields
  const columnMappingOptions = getColumnMappingOptions(election, allowedPartyKeys, allowedPartyOptions);
  const requiredFields = getRequiredFields(election, allowedPartyKeys, allowedPartyOptions);

  // Filter rows with validation errors
  const rowsWithErrors = useMemo(() => {
    if (!cleanedData) return [];
    return cleanedData.filter(row => 
      Object.values(row.validationErrors).some(error => error !== null)
    );
  }, [cleanedData]);

  // Determine which data to display based on filter
  const displayData = showOnlyErrors ? rowsWithErrors : cleanedData || [];

  // Extract unique office keys from labeled data
  const uniqueOfficeKeys = useMemo(() => {
    if (!labeledData) return [];
    
    const officeKeys = labeledData
      .map(label => label.office)
      .filter((office): office is string => Boolean(office));
    
    return [...new Set(officeKeys)];
  }, [labeledData]);

  // Extract unique district IDs from labeled data
  const uniqueDistrictIds = useMemo(() => {
    if (!labeledData) return [];
    
    const districtIds = labeledData
      .map(label => label.district)
      .filter((district): district is string => Boolean(district));
    
    return [...new Set(districtIds)];
  }, [labeledData]);
  const uniqueOfficeKeysSignature = uniqueOfficeKeys.join('\u0000');
  const uniqueDistrictIdsSignature = uniqueDistrictIds.join('\u0000');

  // Calculate labeling progress
  const labelingProgress = useMemo(() => {
    if (!labeledData || labeledData.length === 0) {
      return { labeled: 0, total: 0, percentage: 0 };
    }
    
    const labeledCount = labeledData.filter(label => label.office !== null && label.office !== undefined).length;
    const totalCount = labeledData.length;
    const percentage = totalCount > 0 ? (labeledCount / totalCount) * 100 : 0;
    
    return {
      labeled: labeledCount,
      total: totalCount,
      percentage
    };
  }, [labeledData]);

  // Calculate district labeling progress
  const districtLabelingProgress = useMemo(() => {
    if (!labeledData || labeledData.length === 0) {
      return { labeled: 0, total: 0, percentage: 0 };
    }
    
    const labeledCount = labeledData.filter(label => label.district !== null && label.district !== undefined).length;
    const totalCount = labeledData.length;
    const percentage = totalCount > 0 ? (labeledCount / totalCount) * 100 : 0;
    
    return {
      labeled: labeledCount,
      total: totalCount,
      percentage
    };
  }, [labeledData]);

  const labeledDataByGroupLabel = useMemo(
    () => indexFirstBy(labeledData, label => label.groupLabel),
    [labeledData]
  );
  const officeDataByKey = useMemo(
    () => indexFirstBy(officeData, office => office.key),
    [officeData]
  );
  const districtDataById = useMemo(
    () => indexFirstBy(districtData, district => district._id),
    [districtData]
  );
  const matchedDataByGroupParty = useMemo(
    () => indexFirstBy(matchedData, match => getGroupPartyKey(match.groupLabel, match.party)),
    [matchedData]
  );
  const officialDataByKey = useMemo(
    () => indexFirstBy(officialData, official => official.key),
    [officialData]
  );
  const raceDataById = useMemo(
    () => indexFirstBy(raceData, race => race._id),
    [raceData]
  );

  // Zip groupedData with labeledData and office data
  const groupedDataWithLabels = useMemo(() => {
    if (displayedStatus !== 'grouped' || !groupedData) return [];
    return groupedData.map((group, index) => {
      const labelData = labeledDataByGroupLabel.get(group.groupLabel);
      const officeInfo = labelData?.office ? officeDataByKey.get(labelData.office) : undefined;
      const districtInfo = labelData?.district ? districtDataById.get(labelData.district) : undefined;
      
      return {
        ...group,
        office: labelData?.office,
        officeInfo: officeInfo,
        district: labelData?.district,
        districtInfo: districtInfo,
        suggestsOfficeCreationAs: labelData?.suggestsOfficeCreationAs,
        suggestsDistrictCreationAs: labelData?.suggestsDistrictCreationAs,
        officeConfidence: labelData?.officeConfidence,
        districtConfidence: labelData?.districtConfidence,
        labeledBy: labelData?.labeledBy,
        position: labelData?.position,
        groupedDataIndex: index
      };
    });
  }, [displayedStatus, groupedData, labeledDataByGroupLabel, officeDataByKey, districtDataById]) as GroupedDataWithLabels[];

  // Zip matched data with labeled and group data for step 4
  const matchedDataWithLabels = useMemo(() => {
    if (displayedStatus !== 'labeled' || !labeledData || !groupedData) return [];
    
    return groupedData.map(group => {
      const labelData = labeledDataByGroupLabel.get(group.groupLabel);
      const matchData = matchedDataByGroupParty.get(getGroupPartyKey(group.groupLabel, group.party));

      // enrich each matchData with officialInfo
      const officialInfo = matchData?.officialMatches?.map(match => {
        const official = match.official ? officialDataByKey.get(match.official) : undefined;
        return {
          ...match,
          officialInfo: official
        };
      });
      
      // enrich matchData with raceInfo
      const raceInfo = matchData?.raceMatch?.race ? raceDataById.get(matchData.raceMatch.race) : null;
      
      return {
        ...group,
        ...labelData,
        matchData: {
          ...matchData,
          officialMatches: officialInfo,
          raceInfo: raceInfo
        }
      };
    });
  }, [displayedStatus, labeledData, groupedData, labeledDataByGroupLabel, matchedDataByGroupParty, officialDataByKey, raceDataById]) as Array<GroupedData & LabeledData & { matchData?: MatchedData & { raceInfo?: any }; officeInfo?: Office; districtInfo?: District }>;

  // Create flattened table data with race rows and candidate rows
  const flattenedMatchData = useMemo(() => {
    if (!matchedDataWithLabels) return [];
    
    const flattened: Array<{
      type: 'race' | 'candidate';
      groupIndex: number;
      groupData: GroupedData & LabeledData & { 
        matchData?: MatchedData & { raceInfo?: any };
        officeInfo?: Office; 
        districtInfo?: District;
      };
      candidate?: CandidateListCandidate;
      candidateIndex?: number;
    }> = [];
    
    matchedDataWithLabels.forEach((group, groupIndex) => {
      // Add race row
      flattened.push({
        type: 'race',
        groupIndex,
        groupData: group,
      });
      
      // Add candidate rows
      if (group.candidates && Array.isArray(group.candidates)) {
        group.candidates.forEach((candidate: CandidateListCandidate, candidateIndex: number) => {
          flattened.push({
            type: 'candidate',
            groupIndex,
            groupData: group,
            candidate,
            candidateIndex
          });
        });
      }
    });
    
    return flattened;
  }, [matchedDataWithLabels]);

  // Detect duplicate rows and conflicts
  const duplicateAnalysis = useMemo(() => {
    if (!groupedDataWithLabels) return { duplicates: [], needsAttention: [] };
    const duplicates: Array<{ groups: GroupedDataWithLabels[], reason: string }> = [];
    const needsAttention: Set<string> = new Set();
    
    // Group by office + district + party combination
    const groupsByKey = new Map<string, GroupedDataWithLabels[]>();
    
    groupedDataWithLabels.forEach(group => {
      const key = `${group.office || 'no-office'}|${group.district || 'no-district'}|${group.party || 'no-party'}`;
      if (!groupsByKey.has(key)) {
        groupsByKey.set(key, []);
      }
      groupsByKey.get(key)!.push(group);
    });
    
    // Check for duplicates and conflicts
    groupsByKey.forEach((groups, key) => {
      if (groups.length > 1) {
        const seatIdentifiers = groups.map(g => `${g.position?.seatIdentifier} - ${g.position?.special ? 'special' : 'not-special'}`);
        const seatIdentifiersUnique = [...new Set(seatIdentifiers)];

        if(seatIdentifiers.length > seatIdentifiersUnique.length) {
          // groups.forEach(g => {
          //   if(!g.position?.seatIdentifier) {
          //     needsAttention.add(g.groupLabel);
          //   }
          // });

          duplicates.push({
            groups,
            reason: 'conflicting-seat-identifiers'
          });

          // add every group to needs attention
          groups.forEach(g => needsAttention.add(g.groupLabel));
        }
      }
    });
    
    return { duplicates, needsAttention: Array.from(needsAttention) };
  }, [groupedDataWithLabels]);
  const duplicateNeedsAttention = useMemo(
    () => new Set(duplicateAnalysis.needsAttention),
    [duplicateAnalysis.needsAttention]
  );
  const duplicateByGroupLabel = useMemo(() => {
    const index = new Map<string, typeof duplicateAnalysis.duplicates[number]>();
    duplicateAnalysis.duplicates.forEach(duplicate => {
      duplicate.groups.forEach(group => {
        if (!index.has(group.groupLabel)) {
          index.set(group.groupLabel, duplicate);
        }
      });
    });
    return index;
  }, [duplicateAnalysis.duplicates]);

  // Detect duplicate official matches - no two candidates should be matched to the same official
  const duplicateOfficialMatches = useMemo(() => {
    if (!matchedDataWithLabels) return { duplicates: [], needsAttention: new Set<string>() };
    
    const officialKeyToCandidates: Record<string, Array<{ candidateName: string; groupLabel: string; party?: string }>> = {};
    
    matchedDataWithLabels.forEach(group => {
      group.matchData?.officialMatches?.forEach(match => {
        // Only check matches to existing officials (not create operations)
        if(match.official && !match.create) {
          const officialKey = match.official;
          if(!officialKeyToCandidates[officialKey]) {
            officialKeyToCandidates[officialKey] = [];
          }
          officialKeyToCandidates[officialKey].push({
            candidateName: match.candidateName,
            groupLabel: group.groupLabel,
            party: group.party
          });
        }
      });
    });
    
    const duplicateOfficials = Object.keys(officialKeyToCandidates).filter(officialKey => officialKeyToCandidates[officialKey].length > 1);
    const needsAttention = new Set<string>();
    
    duplicateOfficials.forEach(officialKey => {
      const candidates = officialKeyToCandidates[officialKey];
      candidates.forEach(candidate => {
        needsAttention.add(`${candidate.groupLabel}${candidate.party ? `-${candidate.party}` : ''}-${candidate.candidateName}`);
      });
    });
    
    return {
      duplicates: duplicateOfficials.map(officialKey => ({
        officialKey,
        candidates: officialKeyToCandidates[officialKey]
      })),
      needsAttention
    };
  }, [matchedDataWithLabels]);

  // Filter data based on needs attention filter
  const indecesNeedingLabelingAttention = useMemo(() => {
    if (!groupedDataWithLabels) return [];
    return groupedDataWithLabels.map((group, index) => 
      (
        (!group.office || (group.officeConfidence !== undefined && group.officeConfidence < 0.5)) || 
        (!group.district || (group.districtConfidence !== undefined && group.districtConfidence < 0.5)) ||
        duplicateNeedsAttention.has(group.groupLabel)
      ) ? index : -1
    ).filter(index => index !== -1);
  }, [groupedDataWithLabels, duplicateNeedsAttention]);
  const indicesNeedingLabelingAttention = useMemo(
    () => new Set(indecesNeedingLabelingAttention),
    [indecesNeedingLabelingAttention]
  );

  const attentionFilteredGroupedData = useMemo(() => {
    if (!groupedDataWithLabels) return [];
    
    if (showOnlyNeedsAttention) {
      return groupedDataWithLabels.filter((group, index) => indicesNeedingLabelingAttention.has(index));
    }
    
    return groupedDataWithLabels;
  }, [groupedDataWithLabels, showOnlyNeedsAttention, indicesNeedingLabelingAttention]);
  const filteredGroupedData = useMemo(
    () => filterVerifyDataRows(attentionFilteredGroupedData, verifyDataSearch),
    [attentionFilteredGroupedData, verifyDataSearch]
  );
  const verifyDataPageData = useMemo(
    () => getVerifyDataPage(filteredGroupedData, verifyDataPage),
    [filteredGroupedData, verifyDataPage]
  );
  const allVisibleGroupedDataRowsSelected = verifyDataPageData.rows.length > 0 &&
    verifyDataPageData.rows.every(group => selectedGroupedDataRows.has(group.groupedDataIndex));
  const someVisibleGroupedDataRowsSelected = verifyDataPageData.rows.some(
    group => selectedGroupedDataRows.has(group.groupedDataIndex)
  );

  useEffect(() => {
    setVerifyDataPage(1);
  }, [showOnlyNeedsAttention, verifyDataSearch]);

  useEffect(() => {
    if (verifyDataPage !== verifyDataPageData.page) {
      setVerifyDataPage(verifyDataPageData.page);
    }
  }, [verifyDataPage, verifyDataPageData.page]);

  useEffect(() => {
    if (displayedStatus !== 'grouped') return;

    const focusVerifyDataSearch = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'f') return;
      event.preventDefault();
      verifyDataSearchInputRef.current?.focus();
      verifyDataSearchInputRef.current?.select();
    };

    window.addEventListener('keydown', focusVerifyDataSearch);
    return () => window.removeEventListener('keydown', focusVerifyDataSearch);
  }, [displayedStatus]);

  /*
     Common functions related to how we track the user's progress through the steps
  */
  const getCurrentStep = () => {
    return displayedStepIndex;
  };

  const navigateToStep = (stepName: typeof displayedStepName) => {
    if (!candidateListId) return;
    const path = `/elections/${electionKey}/candidate-lists/${candidateListId}/${stepName}`;
    history.push(path);
  };

  const getSteps = () => {
    const progressStepIndex = getProgressStepIndex(
      candidateList?.status,
      candidateListId === 'new'
    );

    return CANDIDATE_LIST_STEPS.map((step, index) => {
      const available = isStepAvailable(
        step.routeName,
        candidateList?.status,
        candidateListId === 'new'
      );
      const reviewStepIsNavigable = index >= 2 && Boolean(candidateList?._id);

      return {
        name: step.name,
        isComplete: index < progressStepIndex || (
          index === CANDIDATE_LIST_STEPS.length - 1 &&
          candidateList?.status === 'created'
        ),
        disabled: !available,
        onClick: reviewStepIsNavigable && available
          ? () => navigateToStep(step.routeName)
          : undefined
      };
    });
  };

  const renderCandidateListToolbar = (isNew = false) => {
    const moreButtonOptions = displayedStatus === 'grouped' ? [{
      text: 'Re-run entity matching',
      icon: <RefreshIcon fontSize="small" />,
      disabled: isRematchingEntities ||
        candidateList?.processInProgress?.stage !== 'none',
      onClick: () => {
        setEntityRematchError(null);
        setShowEntityRematchModal(true);
      }
    }] : displayedStatus === 'labeled' ? [{
      text: 'Clear existing matches and then match',
      icon: <RefreshIcon fontSize="small" />,
      disabled: isRetryingMatching ||
        candidateList?.processInProgress?.stage !== 'none',
      onClick: () => handleMatchingRetry('all')
    }, {
      text: 'Match only unmatched data',
      icon: <RefreshIcon fontSize="small" />,
      disabled: isRetryingMatching ||
        candidateList?.processInProgress?.stage !== 'none' ||
        !hasUnmatchedMatchingData(raceStats, candidateStats),
      onClick: () => handleMatchingRetry('unmatched')
    }] : undefined;

    return (
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}` } }}
        navTree={[
          { text: 'Elections', to: { pathname: '/elections' } },
          { text: election?.name, to: { pathname: `/elections/${electionKey}` } },
          { text: isNew ? 'New Candidate List' : 'Candidate List' }
        ]}
        navigationTrailingComponent={(
          <StepTracker
            steps={getSteps()}
            currentStep={getCurrentStep()}
            compact
            style={{ width: '620px', maxWidth: '55vw', paddingRight: '40px' }}
          />
        )}
        MoreButton={moreButtonOptions}
      />
    );
  };

  const syncCandidateListResponse = (candidateListData: CandidateList) => {
    const nextCandidateList = candidateListData;
    setCandidateList(nextCandidateList);

    if (candidateListData.status === 'started' && candidateListData.rawCsvData) {
      const filteredData = (candidateListData.rawCsvData as Record<string, any>[])
        .filter((row: Record<string, any>) => !isRowEmpty(row));
      const headers = filteredData.length > 0 ? Object.keys(filteredData[0]) : [];
      const existingColumns = candidateListData.labeledColumns || [];
      const columns = existingColumns.length === 0 || existingColumns.every(col => !col.columnLabel)
        ? autoMapColumns(headers)
        : existingColumns;

      setRawCsvData(filteredData);
      setCsvHeaders(headers);
      setLabeledColumns(columns);
      setCleanedData(cleanAndValidateRawData(filteredData, columns));
    }
    setSource(candidateListData.source || '');
    setGroupedData(candidateListData.groupedData || []);
    setLabeledData(candidateListData.labeledData || []);
    setMatchedData(candidateListData.matchedData || []);
    setDataOperations(candidateListData.dataOperations || []);

    return nextCandidateList;
  };

  const patchCandidateList = async (data: PatchCandidateList) => {
    if (!candidateList) {
      throw new Error('Cannot patch a candidate list before its status has loaded.');
    }

    const response = await feathers.getService('candidate-lists').patch(candidateList._id, data);
    syncCandidateListResponse(response);
    return response;
  };

  const refreshElectionData = async () => {
    try {
      const newData = await feathers.getService('elections').get(electionKey);
      setElection(newData);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStateElectionConfig = async (stateCode?: string) => {
    if (!stateCode) {
      setStateElectionConfig(null);
      return;
    }

    try {
      const config = await feathers.getService('state-election-configurations').get(stateCode.toUpperCase());
      setStateElectionConfig(config);
    } catch (error) {
      console.error('Error fetching state election configuration:', error);
      setStateElectionConfig(null);
    }
  };

  /***
   * Step 1: Upload Data
   */
  const handleCSVProcess = (result: any) => {
    // Filter out rows that have all empty data
    const filteredData = result.data.filter((row: Record<string, any>) => !isRowEmpty(row));
    setRawCsvData(filteredData);
    setCsvHeaders(result.headers);
  };

  const handleCSVError = (error: string) => {
    console.error('CSV processing error:', error);
  };

  const createCandidateList = async () => {
    if (!rawCsvData || !csvHeaders || !source.trim() || !election) {
      return;
    }

    setIsCreating(true);
    try {
      const candidateListData: CreateCandidateList = {
        election: electionKey!,
        state: election.state,
        source,
        createdByUser: user._id,
        status: 'started',
        rawCsvData,
        labeledColumns: autoMapColumns(csvHeaders),
        groupedData: [],
        labeledData: []
      };

      const result = await feathers.getService('candidate-lists').create(candidateListData);
      console.log('Candidate list created:', result);
      
      // Redirect to the created candidate list
      window.location.href = `/elections/${electionKey}/candidate-lists/${result._id}/map-columns`;
    } catch (error) {
      console.error('Error creating candidate list:', error);
      setIsCreating(false);
    }
  };



  /***
   * Step 2: mapping columns and validating data
   */
  const getMappingProgress = () => {
    if (!labeledColumns.length) return { mapped: 0, total: 0, requiredMapped: 0, requiredTotal: requiredFields.length };
    
    const mappedFields = labeledColumns.filter(col => col.columnLabel).length;
    const requiredMapped = labeledColumns.filter(col => 
      col.columnLabel && requiredFields.some(field => field.value === col.columnLabel)
    ).length;
    
    return {
      mapped: mappedFields,
      total: labeledColumns.length,
      requiredMapped,
      requiredTotal: requiredFields.length
    };
  };

  const isAllRequiredMapped = () => {
    const progress = getMappingProgress();
    return progress.requiredMapped >= progress.requiredTotal;
  };

  // Check if submission is ready (no validation errors, all required fields mapped)
  const isMappingComplete = useMemo(() => {
    if (!cleanedData || !isAllRequiredMapped()) return false;
    return rowsWithErrors.length === 0;
  }, [cleanedData, rowsWithErrors]);

  // Calculate unique races and candidates
  const getUniqueRacesAndCandidates = useMemo(() => {
    if (!cleanedData) return { uniqueRaces: 0, uniqueCandidates: 0 };
    
    const uniqueCandidates = new Set();
    const uniqueRaces = new Set();
    
    cleanedData.forEach(row => {
      // Count unique candidates (by name)
      if (row.cleanedData.name) {
        uniqueCandidates.add(row.cleanedData.name);
      }
      
      // Count unique races
      if (row.cleanedData.groupLabel) {
        let raceKey = row.cleanedData.groupLabel;
        
        // For partisan elections, include party in race identification
        if (election?.partisan && row.cleanedData.raceParty) {
          raceKey = `${row.cleanedData.groupLabel} - ${row.cleanedData.raceParty}`;
        }
        
        uniqueRaces.add(raceKey);
      }
    });
    
    return {
      uniqueRaces: uniqueRaces.size,
      uniqueCandidates: uniqueCandidates.size
    };
  }, [cleanedData, election?.partisan]);

  // Handle mapping completion
  const handleMappingComplete = () => {
    setMappingConfirmationError(null);
    setShowConfirmationModal(true);
  };

  const handleCloseMappingConfirmation = () => {
    if (isConfirmingMapping) return;
    setMappingConfirmationError(null);
    setShowConfirmationModal(false);
  };

  // Toggle row expansion
  const toggleRowExpansion = (groupedDataIndex: number) => {
    setExpandedRows(previousExpandedRows => {
      const nextExpandedRows = new Set(previousExpandedRows);
      if (nextExpandedRows.has(groupedDataIndex)) {
        nextExpandedRows.delete(groupedDataIndex);
      } else {
        nextExpandedRows.add(groupedDataIndex);
      }
      return nextExpandedRows;
    });
  };

  const toggleGroupedDataRowSelection = (groupedDataIndex: number, selected: boolean) => {
    setSelectedGroupedDataRows(previousSelectedRows => {
      const nextSelectedRows = new Set(previousSelectedRows);
      if (selected) {
        nextSelectedRows.add(groupedDataIndex);
      } else {
        nextSelectedRows.delete(groupedDataIndex);
      }
      return nextSelectedRows;
    });
  };

  const handleDeleteGroupedDataRow = async (groupedDataIndex: number) => {
    if (
      !candidateList ||
      !groupedData ||
      isDeletingGroupedDataRows ||
      groupedDataIndex < 0 ||
      groupedDataIndex >= groupedData.length
    ) return;

    const previousGroupedData = groupedData;
    const updatedGroupedData = groupedData.filter((_, index) => index !== groupedDataIndex);

    setGroupedDataDeletionError(null);
    setGroupedData(updatedGroupedData);
    setCandidateList(prevCandidateList => (
      prevCandidateList ? { ...prevCandidateList, groupedData: updatedGroupedData } : prevCandidateList
    ));
    setExpandedRows(new Set());
    setSelectedGroupedDataRows(new Set());
    setIsDeletingGroupedDataRows(true);

    try {
      const response = await patchCandidateList({
        groupedData: updatedGroupedData
      });

      setGroupedData(response.groupedData || updatedGroupedData);
    } catch (error) {
      console.error('Error deleting grouped data row:', error);
      setGroupedDataDeletionError(getErrorMessage(error, 'Could not delete the selected row.'));
      setGroupedData(previousGroupedData);
      setCandidateList(prevCandidateList => (
        prevCandidateList ? { ...prevCandidateList, groupedData: previousGroupedData } : prevCandidateList
      ));
    } finally {
      setIsDeletingGroupedDataRows(false);
    }
  };

  const handleDeleteSelectedGroupedDataRows = async () => {
    if (!candidateList || !groupedData || selectedGroupedDataRows.size === 0) return;

    const previousGroupedData = groupedData;
    const updatedGroupedData = groupedData.filter(
      (_, index) => !selectedGroupedDataRows.has(index)
    );

    setIsDeletingGroupedDataRows(true);
    setGroupedDataDeletionError(null);
    setGroupedData(updatedGroupedData);
    setCandidateList(previousCandidateList => (
      previousCandidateList
        ? { ...previousCandidateList, groupedData: updatedGroupedData }
        : previousCandidateList
    ));
    setExpandedRows(new Set());

    try {
      const response = await patchCandidateList({
        groupedData: updatedGroupedData
      });

      setGroupedData(response.groupedData || updatedGroupedData);
      setSelectedGroupedDataRows(new Set());
    } catch (error) {
      console.error('Error deleting selected grouped data rows:', error);
      setGroupedDataDeletionError(getErrorMessage(error, 'Could not delete the selected rows.'));
      setGroupedData(previousGroupedData);
      setCandidateList(previousCandidateList => (
        previousCandidateList
          ? { ...previousCandidateList, groupedData: previousGroupedData }
          : previousCandidateList
      ));
    } finally {
      setIsDeletingGroupedDataRows(false);
    }
  };

  // Handle confirmation action
  const handleConfirmMapping = async () => {
    if (!candidateList || isConfirmingMapping) return;
    
    try {
      setIsConfirmingMapping(true);
      setMappingConfirmationError(null);
      await patchCandidateList({
        status: 'grouped'
      });
      navigateToStep('verify-data');
      
      // Close the modal
      setShowConfirmationModal(false);
      
      console.log('Candidate list status updated to grouped');
    } catch (error) {
      console.error('Error updating candidate list status:', error);
      setMappingConfirmationError(getErrorMessage(error, 'Failed to confirm mapping completion. Please try again.'));
    } finally {
      setIsConfirmingMapping(false);
    }
  };

  const handleEntityRematch = async () => {
    if (
      !candidateList ||
      isRematchingEntities ||
      candidateList.processInProgress?.stage !== 'none'
    ) return;

    setIsRematchingEntities(true);
    setEntityRematchError(null);
    try {
      await patchCandidateList({ $rematchEntities: true });
      setShowEntityRematchModal(false);
    } catch (error) {
      console.error('Error restarting candidate list entity matching:', error);
      setEntityRematchError(
        getErrorMessage(error, 'Failed to re-run entity matching. Please try again.')
      );
    } finally {
      setIsRematchingEntities(false);
    }
  };

  const handleMatchingRetry = async (mode: CandidateListRematchMode) => {
    if (
      !candidateList ||
      isRetryingMatching ||
      candidateList.processInProgress?.stage !== 'none'
    ) return;
    
    setIsRetryingMatching(true);
    setMatchingRetryError(null);
    try {
      await patchCandidateList(getCandidateListRematchCommand(mode));
      console.log('Candidate list matching restarted');
    } catch (error) {
      console.error('Error restarting candidate list matching:', error);
      setMatchingRetryError(getErrorMessage(error, 'Failed to restart matching. Please try again.'));
    } finally {
      setIsRetryingMatching(false);
    }
  };

  const handleCancelMatching = async () => {
    if (
      !candidateList ||
      isCancellingMatching ||
      !['labeling', 'matching'].includes(candidateList.processInProgress?.stage || 'none')
    ) return;

    setIsCancellingMatching(true);
    setMatchingCancellationError(null);
    try {
      await patchCandidateList({
        $cancelMatching: true
      });
    } catch (error) {
      console.error('Error cancelling candidate list matching:', error);
      setMatchingCancellationError(getErrorMessage(error, 'Failed to cancel entity matching. Please try again.'));
    } finally {
      setIsCancellingMatching(false);
    }
  };

  // Handle labeling completion
  const handleLabelingComplete = async () => {
    if (!candidateList) return;

    if (candidateList.status !== 'grouped') {
      navigateToStep('merge-with-existing');
      return;
    }
    
    try {
      await patchCandidateList({
        status: 'labeled'
      });
      navigateToStep('merge-with-existing');

      console.log('Candidate list status updated to labeled');
    } catch (error) {
      console.error('Error updating candidate list status:', error);
      // Optionally show an error message to the user
    }
  };

  const markDataAsMatched = async () => {
    console.log('marked data as matched')
    if (!candidateList) return;

    if (candidateList.status !== 'labeled') {
      navigateToStep('implementation-districts');
      return;
    }

    try {
      await patchCandidateList({ status: 'matched' });
      navigateToStep('implementation-districts');
    } catch (error) {
      console.error('Error updating candidate list status:', error);
    }
  };

  const confirmScouting = async () => {
    if (!candidateList) return;

    if (candidateList.status !== 'matched') {
      navigateToStep('data-operations');
      return;
    }

    try {
      await patchCandidateList({
        status: 'scouted',
        scoutingConfiguration: candidateList.scoutingConfiguration || [],
        estimatedScoutingMinutes: candidateList.estimatedScoutingMinutes ?? null
      });
      navigateToStep('data-operations');
    } catch (error) {
      console.error('Error confirming scouting:', error);
    }
  };

  const handleCreateEntities = async () => {
    if (!candidateList) return;

    try {
      await patchCandidateList({
        $createEntities: true
      });

      console.log('Entity creation started');
    } catch (error) {
      console.error('Error starting entity creation:', error);
      // Optionally show an error message to the user
    }
  };

  const handleCancelCreateEntities = async () => {
    if (!candidateList || candidateList.processInProgress?.stage !== 'creating') return;

    setIsCancellingCreate(true);
    try {
      await patchCandidateList({
        $cancelCreateEntities: true
      });
    } catch (error) {
      console.error('Error cancelling entity creation:', error);
    } finally {
      setIsCancellingCreate(false);
    }
  };

  const handleRegenerateDataOperations = async () => {
    if (!candidateList || isCreatingDataOperations || isRegeneratingDataOperations) return;

    setIsRegeneratingDataOperations(true);
    setRegenerateDataOperationsError(null);
    try {
      await patchCandidateList({
        $regenerateDataOperations: true
      });
    } catch (error) {
      console.error('Error regenerating data operations:', error);
      setRegenerateDataOperationsError(
        getErrorMessage(error, 'Unable to regenerate data operations.')
      );
    } finally {
      setIsRegeneratingDataOperations(false);
    }
  };

  const saveDataOperations = async (operations: DataOperation[]) => {
    console.log('saveDataOperations', operations);
    if (!candidateList) return;
    
    const operationsRevert = dataOperations?.slice() || [];
    setDataOperations(operations);

    try {
      await patchCandidateList({
        dataOperations: operations,
        dataOperationsError: null
      });
    } catch (error) {
      console.error('Error saving data operations:', error);
      setDataOperations(operationsRevert);
    }
  };

  const changeOfficialMatch = async (change: ChangeOfficialMatchPayload) => {
    if (!candidateList) {
      throw new Error('Cannot change an official match before the candidate list has loaded.');
    }

    const matchedRace = (candidateList.matchedData || []).find(match => {
      const labeled = (candidateList.labeledData || [])
        .find(item => item.groupLabel === match.groupLabel);
      if (!labeled) return false;
      const uniqueId = `${labeled.office}-${labeled.district}-${match.party ?? 'no-party'}-${labeled.position?.seatIdentifier ?? 'no-seat-identifier'}-${labeled.position?.special ? 'special' : 'not-special'}`;
      return uniqueId === change.raceUniqueId;
    });
    if (!matchedRace) {
      throw new Error('The candidate race could not be located. Refresh and try again.');
    }

    await patchCandidateList({
      $changeOfficialMatch: {
        locator: {
          groupLabel: matchedRace.groupLabel,
          candidateName: change.candidateName,
          ...(matchedRace.party !== undefined ? { party: matchedRace.party } : {})
        },
        previousOfficial: change.previousOfficial,
        nextOfficial: change.nextOfficial
      }
    });
  };

  // Auto-map columns based on header matches
  const autoMapColumns = (headers: string[]) => {
    const currentColumnMappingOptions = getColumnMappingOptions(election, allowedPartyKeys, allowedPartyOptions);
    
    return headers.map(header => {
      // Normalize header for comparison (lowercase, remove spaces/special chars)
      const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
      
      // Find matching option
      const matchingOption = currentColumnMappingOptions.find(option => {
        const normalizedLabel = option.label.toLowerCase().replace(/[^a-z0-9]/g, '');
        const normalizedValue = option.value.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        // Check if header matches label or value
        return normalizedHeader === normalizedLabel || normalizedHeader === normalizedValue;
      });
      
      return {
        columnName: header,
        columnLabel: getColumnLabel(matchingOption?.value),
        discardColumn: false
      };
    });
  };

  // Process raw CSV data using mapping and validation functions
  const cleanAndValidateRawData = (rawData: any[], columns: LabeledColumn[]): {
    rawData: Record<string, string>;
    cleanedData: Record<ColumnLabel, any>;
    validationErrors: Record<ColumnLabel, string | null>;
  }[] => {
    const currentOptions = getColumnMappingOptions(election, allowedPartyKeys, allowedPartyOptions);
    
    return rawData.map(row => {
             const processedRow = {
         rawData: row,
         cleanedData: {} as Record<ColumnLabel, any>,
         validationErrors: {} as Record<ColumnLabel, string | null>
       }
      
      columns.forEach(column => {
        if (column.discardColumn || !column.columnLabel) {
          return; // Skip discarded or unmapped columns
        }
        
        const rawValue = row[column.columnName];
        let mappedValue;

        // Find the mapping function for this column type
        const option = currentOptions.find(opt => opt.value === column.columnLabel);
        if (option && option.map) {
          mappedValue = option.map(rawValue);
          processedRow.cleanedData[column.columnLabel] = mappedValue;
        } else {
          // Fallback: keep original value
          mappedValue = rawValue;
          processedRow.cleanedData[column.columnLabel] = rawValue;
        }

        if (option?.validate) {
          const validationResult = option.validate(mappedValue);
          if (validationResult !== true) {
            processedRow.validationErrors[column.columnLabel] = validationResult as string;
          }
        }
      });
      
      return processedRow;
    });
  };

  const handlePartyRemedy = async ({
    columnName,
    rawValue,
    party,
  }: {
    columnName: string;
    rawValue: string;
    party: Pick<Party, 'key' | 'name'>;
  }) => {
    if (!rawCsvData) return;

    const updatedRawCsvData = rawCsvData.map((row) => {
      if (String(row[columnName] ?? '') !== String(rawValue ?? '')) {
        return row;
      }

      return {
        ...row,
        [columnName]: party.name,
      };
    });

    setRawCsvData(updatedRawCsvData);
    setCleanedData(cleanAndValidateRawData(updatedRawCsvData, labeledColumns));

    if (candidateList) {
      try {
        await patchCandidateList({
          rawCsvData: updatedRawCsvData,
        });
      } catch (error) {
        console.error('Error saving corrected party values:', error);
      }
    }
  };

  const fetchOfficeData = useCallback(async (officeKeys: string[]) => {
    if (officeKeys.length === 0) {
      setOfficeData([]);
      return;
    }
    
    try {
      // break into chunks of 15
      const chunkSize = 15;
      const chunks = [];
      for(let i = 0; i < officeKeys.length; i += chunkSize) {
        chunks.push(officeKeys.slice(i, i + chunkSize));
      }

      const response = await Promise.all(chunks.map(chunk => feathers.getService('offices').find({
        query: { key: { $in: chunk }, $limit: chunkSize }
      })));
      setOfficeData(response.flatMap(r => r.data || []) || []);
    } catch (error) {
      console.error('Error fetching office data:', error);
      setOfficeData([]);
    }
  }, [feathers]);

  const fetchDistrictData = useCallback(async (districtIds: string[]) => {
    if (districtIds.length === 0) {
      setDistrictData([]);
      return;
    }
    
    try {
      // need to chunk out into 50 at a time
      const chunkSize = 20;
      const chunks = [];
      for(let i = 0; i < districtIds.length; i += chunkSize) {
        chunks.push(districtIds.slice(i, i + chunkSize));
      }

      const response = await Promise.all(chunks.map(chunk => feathers.getService('districts').find({
        query: { _id: chunk },
        $limit: chunkSize
      })));

      const flattenedResponse = response.flatMap(r => r.data);
      setDistrictData(flattenedResponse || []);
    } catch (error) {
      console.error('Error fetching district data:', error);
      setDistrictData([]);
    }
  }, [feathers]);

  const fetchOfficialData = async (officialKeys: string[]) => {
    if (officialKeys.length === 0) {
      return;
    }
    
    const chunkSize = 20;
    const chunks = [];
    for(let i = 0; i < officialKeys.length; i += chunkSize) {
      chunks.push(officialKeys.slice(i, i + chunkSize));
    }

    const response = await Promise.all(chunks.map(chunk => feathers.getService('officials').find({
      query: { key: chunk, $limit: chunkSize }
    })));

    const flattenedResponse = response.flatMap(r => r.data);
    setOfficialData(existingOfficials => {
      const newOfficials = flattenedResponse.filter(official => !existingOfficials?.some(o => o.key === official.key));
      return [...(existingOfficials || []), ...newOfficials];
    });
  }

  const fetchRaceData = async (raceIds: string[]) => {
    if (raceIds.length === 0) {
      return;
    }
    
    const chunkSize = 20;
    const chunks = [];
    for(let i = 0; i < raceIds.length; i += chunkSize) {
      chunks.push(raceIds.slice(i, i + chunkSize));
    }

    const response = await Promise.all(chunks.map(chunk => feathers.getService('races').find({
      query: { _id: chunk, $limit: chunkSize }
    })));

    const flattenedResponse = response.flatMap(r => r.data);
    setRaceData(existingRaces => {
      const newRaces = flattenedResponse.filter(race => !existingRaces?.some(r => r._id === race._id));
      return [...(existingRaces || []), ...newRaces];
    });
  }

  useEffect(() => {
    if(displayedStatus === 'labeled' && matchedData) {
      const officialKeys = matchedData.map(match => match.officialMatches?.map(officialMatch => officialMatch.official)).flat().filter(Boolean) as string[];
      const uniqueOfficialKeys = [...new Set(officialKeys)];
      // Use ref to track what we've already fetched to prevent infinite loops
      const missingOfficials = uniqueOfficialKeys.filter(key => !fetchedOfficialKeysRef.current.has(key)) as string[];
      if (missingOfficials.length > 0) {
        missingOfficials.forEach(key => fetchedOfficialKeysRef.current.add(key));
        fetchOfficialData(missingOfficials);
      }
      
      const raceIds = matchedData.map(match => match.raceMatch?.race).filter(Boolean) as string[];
      const uniqueRaceIds = [...new Set(raceIds)];
      // Use ref to track what we've already fetched to prevent infinite loops
      const missingRaces = uniqueRaceIds.filter(id => !fetchedRaceIdsRef.current.has(id));
      if (missingRaces.length > 0) {
        missingRaces.forEach(id => fetchedRaceIdsRef.current.add(id));
        fetchRaceData(missingRaces);
      }
    }
  }, [displayedStatus, matchedData]);

  const fetchCandidateList = async () => {
    if (!candidateListId || candidateListId === 'new') return;

    try {
      setCandidateList(null);
      const candidateListData = await feathers.getService('candidate-lists').get(candidateListId);
      syncCandidateListResponse(candidateListData);
    } catch (err) {
      console.error('Error fetching candidate list:', err);
    }
  };

  useEffect(() => {
    if (feathers) {
      // Reset refs when loading a new candidate list
      fetchedOfficialKeysRef.current.clear();
      fetchedRaceIdsRef.current.clear();
      refreshElectionData();
      fetchCandidateList();
    }
  }, [feathers, candidateListId]);

  useEffect(() => {
    if (!candidateListId || candidateListId === 'new' || !candidateList) return;
    if (activeStepName !== displayedStepName) {
      history.replace(
        `/elections/${electionKey}/candidate-lists/${candidateListId}/${displayedStepName}`
      );
    }
  }, [
    activeStepName,
    candidateList,
    candidateListId,
    displayedStepName,
    electionKey,
    history
  ]);

  useEffect(() => {
    if (!feathers) return;
    fetchStateElectionConfig(election?.stateCode);
  }, [feathers, election?.stateCode]);

  useEffect(() => {
    if (!feathers) return;
    const fetchPartyOptions = async () => {
      try {
        const response = await feathers.getService('parties').find({
          query: {
            $limit: 1000,
            $sort: { key: 1 }
          }
        });
        setPartyOptions(Array.isArray(response) ? response : response?.data || []);
      } catch (error) {
        console.error('Error fetching parties:', error);
        setPartyOptions([]);
      }
    };

    fetchPartyOptions();
  }, [feathers]);

  useEffect(() => {
    if (!rawCsvData || !labeledColumns.length) return;
    setCleanedData(cleanAndValidateRawData(rawCsvData, labeledColumns));
  }, [allowedPartyKeys]);

  // Fetch office data when unique office keys change
  useEffect(() => {
    if (displayedStatus === 'grouped') {
      fetchOfficeData(uniqueOfficeKeysSignature ? uniqueOfficeKeysSignature.split('\u0000') : []);
    }
  }, [displayedStatus, fetchOfficeData, uniqueOfficeKeysSignature]);

  // Fetch district data when unique district IDs change
  useEffect(() => {
    if (displayedStatus === 'grouped') {
      fetchDistrictData(uniqueDistrictIdsSignature ? uniqueDistrictIdsSignature.split('\u0000') : []);
    }
  }, [displayedStatus, fetchDistrictData, uniqueDistrictIdsSignature]);

  // Update current time every second when background processing is in progress
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    
    if (
      (
        candidateList?.processInProgress?.stage === 'matching' &&
        displayedStatus !== 'grouped'
      ) ||
      candidateList?.processInProgress?.stage === 'creating'
    ) {
      interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [candidateList?.processInProgress?.stage, displayedStatus]);

  // Entity labeling and downstream matching run asynchronously on the server.
  useEffect(() => {
    if (
      !candidateList?._id ||
      !['labeling', 'matching'].includes(
        candidateList.processInProgress?.stage || 'none'
      )
    ) return;

    let isActive = true;
    const pollInterval = setInterval(async () => {
      try {
        const updatedCandidateList = await feathers.getService('candidate-lists').get(candidateList._id);
        if (!isActive) return;

        setCandidateList(updatedCandidateList);
        setGroupedData(updatedCandidateList.groupedData || []);
        setLabeledData(updatedCandidateList.labeledData || []);
        setMatchedData(updatedCandidateList.matchedData || []);
      } catch (error) {
        console.error('Error polling candidate list entity processing:', error);
      }
    }, 5000);

    return () => {
      isActive = false;
      clearInterval(pollInterval);
    };
  }, [candidateList?._id, candidateList?.processInProgress?.stage, feathers]);

  const handleOfficeChange = async (groupLabel: string, newOffice: Office, confidence: number) => {
    // Update officeData state if the new office is not already in the array
    setOfficeData(prevOfficeData => {
      if (!prevOfficeData) return [newOffice];
      const officeExists = prevOfficeData.some(office => office.key === newOffice.key);
      if (officeExists) return prevOfficeData;
      return [...prevOfficeData, newOffice];
    });

    // Now we need to find the corresponding grouplabel in labeledData
    const existingLabel = labeledData?.find(label => label.groupLabel === groupLabel);

    // If the existing label has "suggestsOfficeCreationAs" and it matches the new office name, then we have a new office created
    // in this case, we actually want to update all labels with that same office name
    const newOfficeCreated = existingLabel?.suggestsOfficeCreationAs === newOffice.name;

    let updatedLabeledData: LabeledData[] = (labeledData || []).map((label: LabeledData) => {
      if(label.groupLabel === groupLabel) {
        return {
          ...label,
          office: newOffice.key,
          labeledBy: newOfficeCreated ? 'ai' : 'user',
          officeConfidence: confidence
        }
      }
      return label;
    })

    const groupExists = updatedLabeledData?.some(label => label.groupLabel === groupLabel);
    if(!groupExists) {
      updatedLabeledData.push({ groupLabel, office: newOffice.key, labeledBy: newOfficeCreated ? 'ai' : 'user', officeInfo: newOffice, officeConfidence: confidence } as LabeledData);
    }
    
    // If the new office was created, update all labeledData with the same suggestion
    if(newOfficeCreated) {
      updatedLabeledData = updatedLabeledData.map((label: LabeledData) => {
        if(label.suggestsOfficeCreationAs === newOffice.name) {
          return { ...label, office: newOffice.key, labeledBy: 'ai', officeConfidence: label.officeConfidence ?? 0.1   };
        }
        return label;
      })
    }
    
    // Update labeledData state
    setLabeledData(updatedLabeledData);

    // Save updated labeledData to server
    if (candidateList) {
      try { 
        await patchCandidateList({
          labeledData: updatedLabeledData
        });
      } catch (error) {
        console.error('Error saving office change to server:', error);
        // Optionally revert the local state on error
      }
    }
  };

  const handleDistrictChange = async (groupLabel: string, newDistrict: District, confidence: number) => {
    // add the new district to the districtData state
    setDistrictData(prevDistrictData => {
      if (!prevDistrictData) return [newDistrict];
      const districtExists = prevDistrictData.some(district => district._id === newDistrict._id);
      if (districtExists) return prevDistrictData;
      return [...prevDistrictData, newDistrict];
    });

    // Now we need to find the corresponding grouplabel in labeledData
    const existingLabel = labeledData?.find(label => label.groupLabel === groupLabel);

    // If the existing label has "suggestsDistrictCreationAs" and it matches the new district, then we have a new district created
    const newDistrictCreated = existingLabel?.suggestsDistrictCreationAs && 
      (existingLabel.suggestsDistrictCreationAs.name === newDistrict.name && 
       existingLabel.suggestsDistrictCreationAs.number === newDistrict.number &&
       existingLabel.suggestsDistrictCreationAs.type === newDistrict.type &&
       existingLabel.suggestsDistrictCreationAs.letter === newDistrict.letter &&
       existingLabel.suggestsDistrictCreationAs.parentDistrict === newDistrict.parentId
      );

    let updatedLabeledData: LabeledData[] = (labeledData || []).map((label: LabeledData) => {
      if(label.groupLabel === groupLabel) {
        return {
          ...label,
          district: newDistrict._id,
          labeledBy: newDistrictCreated ? 'ai' : 'user',
          districtConfidence: confidence
        }
      }
      return label;
    })

    const groupExists = updatedLabeledData?.some(label => label.groupLabel === groupLabel);
    if(!groupExists) {
      updatedLabeledData.push({ groupLabel, district: newDistrict._id, labeledBy: newDistrictCreated ? 'ai' : 'user', districtConfidence: confidence } as LabeledData);
    }
    
    // If the new district was created, update all labeledData with the same suggestion
    if(newDistrictCreated) {
      updatedLabeledData = updatedLabeledData.map((label: LabeledData) => {
        if(label.suggestsDistrictCreationAs && 
           (label.suggestsDistrictCreationAs.name === newDistrict.name && 
            label.suggestsDistrictCreationAs.number === newDistrict.number &&
            label.suggestsDistrictCreationAs.type === newDistrict.type &&
            label.suggestsDistrictCreationAs.letter === newDistrict.letter &&
            label.suggestsDistrictCreationAs.parentDistrict === newDistrict.parentId)) {
          return { ...label, district: newDistrict._id, labeledBy: 'ai', districtConfidence: label.districtConfidence ?? 0.1 };
        }
        return label;
      })
    }
    
    // Update labeledData state
    setLabeledData(updatedLabeledData);

    // Save updated labeledData to server
    if (candidateList) {
      try { 
        await patchCandidateList({
          labeledData: updatedLabeledData
        });
        console.log('District change saved to server successfully');
      } catch (error) {
        console.error('Error saving district change to server:', error);
        // Optionally revert the local state on error
      }
    }
  };

  const handleSpecialElectionChange = async (groupLabel: string, isSpecial: boolean) => {
    let updatedLabeledData: LabeledData[] = (labeledData || []).map((label: LabeledData) => {
      if(label.groupLabel === groupLabel) {
        const currentPosition = label.position || { special: false };
        return {
          ...label,
          position: {
            ...currentPosition,
            special: isSpecial
          },
          labeledBy: 'user'
        }
      }
      return label;
    });

    const groupExists = updatedLabeledData?.some(label => label.groupLabel === groupLabel);
    if(!groupExists) {
      updatedLabeledData.push({ 
        groupLabel, 
        position: { special: isSpecial },
        labeledBy: 'user'
      } as LabeledData);
    }
    
    // Update labeledData state
    setLabeledData(updatedLabeledData);

    // Save updated labeledData to server
    if (candidateList) {
      try { 
        await patchCandidateList({
          labeledData: updatedLabeledData
        });
        console.log('Special election change saved to server successfully');
      } catch (error) {
        console.error('Error saving special election change to server:', error);
        // Optionally revert the local state on error
      }
    }
  };

  const handleSeatIdentifierChange = async (groupLabel: string, seatIdentifier: string | null, seatIdentifierType: string | null) => {
    let updatedLabeledData: LabeledData[] = (labeledData || []).map((label: LabeledData) => {
      if(label.groupLabel === groupLabel) {
        const currentPosition = label.position || { special: false };
        return {
          ...label,
          position: {
            special: currentPosition.special,
            ...(seatIdentifier && { seatIdentifier }),
            ...(seatIdentifierType && { seatIdentifierType })
          },
          labeledBy: 'user'
        }
      }
      return label;
    });

    const groupExists = updatedLabeledData?.some(label => label.groupLabel === groupLabel);
    if(!groupExists) {
      updatedLabeledData.push({ 
        groupLabel, 
        position: { 
          special: false,
          ...(seatIdentifier && { seatIdentifier }),
          ...(seatIdentifierType && { seatIdentifierType })
        },
        labeledBy: 'user'
      } as LabeledData);
    }
    
    // Update labeledData state
    setLabeledData(updatedLabeledData);

    // Save updated labeledData to server
    if (candidateList) {
      try { 
        await patchCandidateList({
          labeledData: updatedLabeledData
        });
        console.log('Seat identifier change saved to server successfully');
      } catch (error) {
        console.error('Error saving seat identifier change to server:', error);
        // Optionally revert the local state on error
      }
    }
  };

  const handleRaceChange = async (groupLabel: string, raceMatch: { race: string; createAsNew: boolean } | null) => {
    console.log('handleRaceChange', groupLabel, raceMatch);
    let updatedMatchedData: MatchedData[] = (matchedData || []).map((match: MatchedData) => {
      if(match.groupLabel === groupLabel) {
        return {
          ...match,
          raceMatch: markRaceMatchAsUser(raceMatch)
        };
      }
      return match;
    });

    const groupExists = updatedMatchedData?.some(match => match.groupLabel === groupLabel);
    if(!groupExists) {
      updatedMatchedData.push({ 
        groupLabel, 
        raceMatch: markRaceMatchAsUser(raceMatch),
        party: groupedData?.find(g => g.groupLabel === groupLabel)?.party
      } as MatchedData);
    }
    
    // Update matchedData state
    setMatchedData(updatedMatchedData);

    // Save updated matchedData to server
    if (candidateList) {
      try { 
        await patchCandidateList({
          matchedData: updatedMatchedData
        });

        console.log('Race match change saved to server successfully');
      } catch (error) {
        console.error('Error saving race match change to server:', error);
        // Optionally revert the local state on error
      }
    }
  };

  const handleOfficialChange = async (groupLabel: string, candidateName: string, officialMatch: { official?: Official; create: boolean } | null) => {
    console.log('handleOfficialChange', groupLabel, candidateName, officialMatch);

    const officialKey = officialMatch?.official?.key;

    if(officialMatch?.official) {
      // append official to officialData
      setOfficialData((existingOfficials: Official[] | null) => {
        if(existingOfficials === null) return [officialMatch?.official as Official];
        const newOfficials = existingOfficials?.filter(official => official.key !== officialKey);
        return [...(newOfficials || []), officialMatch?.official as Official];
      });
    }
    
    // Find the group that contains this candidate
    const groupWithCandidate = groupedData?.find(group => 
      group.groupLabel === groupLabel &&
      group.candidates.some((candidate: CandidateListCandidate) => 
        (candidate.name) === candidateName
      )
    );
    
    if (!groupWithCandidate) {
      console.error('Could not find group for candidate:', candidateName);
      return;
    }
    
    let updatedMatchedData: MatchedData[] = (matchedData || []).map((match: MatchedData) => {
      if(match.groupLabel === groupWithCandidate.groupLabel && match.party === groupWithCandidate.party) {
        const officialMatches = (match.officialMatches || []).map((existing: { official?: string; create: boolean; candidateName: string }) => {
          if(existing.candidateName === candidateName) {
            return {
              candidateName,
              ...(officialKey && { official: officialKey }),
              create: officialMatch?.create ?? false
            }
          }
          return existing;
        });

        return ({
          ...match,
          officialMatches
        }) as MatchedData;
      }
      return match;
    });

    const groupExists = updatedMatchedData?.some(match => match.groupLabel === groupWithCandidate.groupLabel);
    if(!groupExists) {
      updatedMatchedData.push({ 
        groupLabel: groupWithCandidate.groupLabel, 
        party: groupWithCandidate.party,
        officialMatches: [
          {
            candidateName,
            official: officialKey,
            create: officialMatch?.create
          }
        ],
      } as MatchedData);
    }
    
    // Update matchedData state
    setMatchedData(updatedMatchedData);

    // Save updated matchedData to server
    if (candidateList) {
      try { 
        await patchCandidateList({
          matchedData: updatedMatchedData
        });

        console.log('Official match change saved to server successfully');
      } catch (error) {
        console.error('Error saving official match change to server:', error);
        // Optionally revert the local state on error
      }
    }
  };

  const handleDeleteMatchedRace = async (groupIndex: number) => {
    if (
      !candidateList ||
      !groupedData ||
      !labeledData ||
      !matchedData ||
      deletingMatchRow ||
      groupIndex < 0 ||
      groupIndex >= groupedData.length
    ) return;

    const groupToDelete = groupedData[groupIndex];
    const rowKey = `race-${getGroupPartyKey(groupToDelete.groupLabel, groupToDelete.party)}`;
    const updatedGroupedData = groupedData.filter((_, index) => index !== groupIndex);
    const groupLabelStillUsed = updatedGroupedData.some(group => group.groupLabel === groupToDelete.groupLabel);
    const updatedLabeledData = groupLabelStillUsed
      ? labeledData
      : labeledData.filter(label => label.groupLabel !== groupToDelete.groupLabel);
    const updatedMatchedData = matchedData.filter(match => (
      getGroupPartyKey(match.groupLabel, match.party) !== getGroupPartyKey(groupToDelete.groupLabel, groupToDelete.party)
    ));

    setDeletingMatchRow(rowKey);
    setMatchRowDeletionError(null);
    setGroupedData(updatedGroupedData);
    setLabeledData(updatedLabeledData);
    setMatchedData(updatedMatchedData);

    try {
      await patchCandidateList({
        groupedData: updatedGroupedData,
        labeledData: updatedLabeledData,
        matchedData: updatedMatchedData
      });
    } catch (error) {
      console.error('Error deleting race from candidate list:', error);
      setGroupedData(groupedData);
      setLabeledData(labeledData);
      setMatchedData(matchedData);
      setMatchRowDeletionError(getErrorMessage(error, 'Failed to delete the race. Please try again.'));
    } finally {
      setDeletingMatchRow(null);
    }
  };

  const handleDeleteMatchedCandidate = async (groupIndex: number, candidateIndex: number) => {
    if (
      !candidateList ||
      !groupedData ||
      !matchedData ||
      deletingMatchRow ||
      groupIndex < 0 ||
      groupIndex >= groupedData.length
    ) return;

    const group = groupedData[groupIndex];
    const candidate = group.candidates?.[candidateIndex];
    if (!candidate) return;

    const rowKey = `candidate-${getGroupPartyKey(group.groupLabel, group.party)}-${candidateIndex}`;
    const updatedGroupedData = groupedData.map((existingGroup, index) => (
      index === groupIndex
        ? {
          ...existingGroup,
          candidates: existingGroup.candidates.filter((_: CandidateListCandidate, index: number) => index !== candidateIndex)
        }
        : existingGroup
    ));
    const updatedMatchedData = matchedData.map(match => (
      getGroupPartyKey(match.groupLabel, match.party) === getGroupPartyKey(group.groupLabel, group.party)
        ? {
          ...match,
          officialMatches: (match.officialMatches || []).filter(officialMatch => (
            officialMatch.candidateName !== candidate.name
          ))
        }
        : match
    ));

    setDeletingMatchRow(rowKey);
    setMatchRowDeletionError(null);
    setGroupedData(updatedGroupedData);
    setMatchedData(updatedMatchedData);

    try {
      await patchCandidateList({
        groupedData: updatedGroupedData,
        matchedData: updatedMatchedData
      });
    } catch (error) {
      console.error('Error deleting candidate from candidate list:', error);
      setGroupedData(groupedData);
      setMatchedData(matchedData);
      setMatchRowDeletionError(getErrorMessage(error, 'Failed to delete the candidate. Please try again.'));
    } finally {
      setDeletingMatchRow(null);
    }
  };

  const handleConfirmMatchRowDeletion = async () => {
    if (!pendingMatchRowDeletion) return;

    const deletion = pendingMatchRowDeletion;
    setPendingMatchRowDeletion(null);

    if (deletion.type === 'race') {
      await handleDeleteMatchedRace(deletion.groupIndex);
    } else {
      await handleDeleteMatchedCandidate(deletion.groupIndex, deletion.candidateIndex);
    }
  };

  const handleColumnLabelChange = async (columnName: string, newLabel: string | undefined) => {
    const updatedColumns = labeledColumns.map(col => 
      col.columnName === columnName 
        ? { 
            ...col, 
            columnLabel: getColumnLabel(newLabel),
            discardColumn: newLabel === 'none' ? true : false
          }
        : col
    );
    
    setLabeledColumns(updatedColumns);
    
    // Process data with new mappings
    if (rawCsvData) {
      const processed = cleanAndValidateRawData(rawCsvData, updatedColumns);
      setCleanedData(processed);
    }
    
    // Auto-save to server
    if (candidateList) {
      try {
        await patchCandidateList({
          labeledColumns: updatedColumns
        });
        console.log('Column mappings auto-saved successfully');
      } catch (error) {
        console.error('Error auto-saving column mappings:', error);
        // Optionally revert the local state on error
        setLabeledColumns(labeledColumns);
      }
    }
  };

  const handleCloseCellEdit = () => {
    if (isSavingCellEdit) return;
    setPendingCellEdit(null);
    setCellEditError(null);
  };

  const handleSaveCellEdit = async () => {
    if (!pendingCellEdit || !rawCsvData || isSavingCellEdit) return;

    const previousRawCsvData = rawCsvData;
    const previousCleanedData = cleanedData;
    const updatedRawCsvData = updateRawCellValue(
      rawCsvData,
      pendingCellEdit.rowIndex,
      pendingCellEdit.columnName,
      pendingCellEdit.value
    );

    setIsSavingCellEdit(true);
    setCellEditError(null);
    setRawCsvData(updatedRawCsvData);
    setCleanedData(cleanAndValidateRawData(updatedRawCsvData, labeledColumns));

    try {
      if (candidateList) {
        await patchCandidateList({ rawCsvData: updatedRawCsvData });
      }
      setPendingCellEdit(null);
    } catch (error) {
      console.error('Error saving mapped cell value:', error);
      setRawCsvData(previousRawCsvData);
      setCleanedData(previousCleanedData);
      setCellEditError(getErrorMessage(error, 'Failed to save the edited value. Please try again.'));
    } finally {
      setIsSavingCellEdit(false);
    }
  };

  // Calculate summary stats for races and candidates
  const raceStats = useMemo(() => {
    if (!matchedDataWithLabels) return { total: 0, matched: 0, new: 0 };
    
    const total = matchedDataWithLabels.length;
    const matched = matchedDataWithLabels.filter(group => 
      group.matchData?.raceMatch?.race && !group.matchData.raceMatch.createAsNew
    ).length;
    const newRaces = matchedDataWithLabels.filter(group => 
      group.matchData?.raceMatch?.createAsNew
    ).length;
    
    return { total, matched, new: newRaces };
  }, [matchedDataWithLabels]);

  const candidateStats = useMemo(() => {
    if (!matchedDataWithLabels) return { total: 0, matched: 0, new: 0 };
    
    let total = 0;
    let matched = 0;
    let newCandidates = 0;
    
    matchedDataWithLabels.forEach(group => {
      if (group.candidates) {
        total += group.candidates.length;
        group.candidates.forEach(candidate => {
          const officialMatch = group.matchData?.officialMatches?.find(match => 
            match.candidateName === candidate.name
          );
          if (officialMatch?.official && !officialMatch.create) {
            matched++;
          } else if (officialMatch?.create) {
            newCandidates++;
          }
        });
      }
    });
    
    return { total, matched, new: newCandidates };
  }, [matchedDataWithLabels]);
  const verifyDataRowHandlersRef = useRef<VerifyDataRowHandlers>({
    toggleExpansion: toggleRowExpansion,
    toggleSelection: toggleGroupedDataRowSelection,
    changeOffice: handleOfficeChange,
    changeDistrict: handleDistrictChange,
    changeSpecialElection: handleSpecialElectionChange,
    changeSeatIdentifier: handleSeatIdentifierChange,
    deleteGroup: handleDeleteGroupedDataRow
  });
  verifyDataRowHandlersRef.current = {
    toggleExpansion: toggleRowExpansion,
    toggleSelection: toggleGroupedDataRowSelection,
    changeOffice: handleOfficeChange,
    changeDistrict: handleDistrictChange,
    changeSpecialElection: handleSpecialElectionChange,
    changeSeatIdentifier: handleSeatIdentifierChange,
    deleteGroup: handleDeleteGroupedDataRow
  };

  if(candidateListId === 'new') {
    return (
      <Wrapper>
        {renderCandidateListToolbar(true)}
        <Content>
          {rawCsvData ? (
            <DataPreviewTile>
              <div style={{ padding: '24px' }}>
                <Typography variant="h3" style={{ marginBottom: '16px' }}>
                  Preview Your Data
                </Typography>
                <Typography variant="body1" style={{ marginBottom: '24px', color: '#666' }}>
                  Review your uploaded CSV data and provide the source information.
                </Typography>
                
                <div style={{ marginBottom: '24px' }}>
                  <TextField
                    fullWidth
                    label="Data Source (URL)"
                    variant="outlined"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder="https://example.com/candidate-data.csv"
                    helperText="Please provide the URL where you obtained this data"
                    style={{ marginBottom: '16px' }}
                  />
                </div>
                
                <Typography variant="h4" style={{ marginBottom: '16px' }}>
                  Data Preview ({rawCsvData.length} rows)
                </Typography>
                
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                      <tr>
                        {csvHeaders?.map((header, index) => (
                          <th key={index} style={{ 
                            padding: '12px 8px', 
                            textAlign: 'left', 
                            borderBottom: '1px solid #ddd',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rawCsvData.slice(0, 40).map((row, rowIndex) => (
                        <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#fafafa' }}>
                          {csvHeaders?.map((header, colIndex) => (
                            <td key={colIndex} style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #eee', 
                              fontSize: '12px',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {row[header] || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {rawCsvData.length > 40 && (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px', textAlign: 'center' }}>
                    Showing first 40 rows of {rawCsvData.length} total rows
                  </Typography>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={createCandidateList}
                    disabled={!source.trim() || isCreating}
                    style={{ minWidth: '120px' }}
                  >
                    {isCreating ? 'Creating...' : 'Next'}
                  </Button>
                </div>
              </div>
            </DataPreviewTile>
          ) : (
          <CSVUploadTile>
            <div style={{ padding: '24px' }}>
              <Typography variant="h2" style={{ marginBottom: '16px' }}>
                Upload Candidate List CSV
              </Typography>
              <Typography variant="body1" style={{ marginBottom: '24px', color: '#666' }}>
                Upload a CSV file containing candidate information. The file should include columns for candidate names, races, and other relevant details.
              </Typography>
              
              <CSVDropzone
                onProcess={handleCSVProcess}
                onError={handleCSVError}
                showPreview={false}
                acceptMessage="Drag and drop a CSV file here, or click to browse files"
              />
            </div>
          </CSVUploadTile>
        )}
      </Content>
    </Wrapper>
    );
  }

  if(candidateList && displayedStatus === 'started') {
    return (
      <Wrapper>
        {renderCandidateListToolbar()}
        
        {candidateList && cleanedData ? (
          <>
            <Typography variant="body1" style={{ marginBottom: '24px', color: '#666' }}>
              Map each column from your CSV to the appropriate field type. This helps organize the candidate data correctly.
            </Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: '24px' }}>
              {getMappingProgress().mapped} out of {getMappingProgress().total} columns mapped
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
              {requiredFields.map((field) => {
                const isMapped = labeledColumns.some(col => col.columnLabel === field.value);
                const fieldLabel = columnMappingOptions.find(opt => opt.value === field.value)?.label.replace(' *', '') || field.value;
                
                return (
                  <StatusIndicator key={field.value} isMapped={isMapped}>
                    {isMapped ? (
                      <CheckCircleIcon style={{ color: '#4caf50', fontSize: '20px' }} />
                    ) : (
                      <CancelIcon style={{ color: '#f44336', fontSize: '20px' }} />
                    )}
                    <Typography variant="body2" style={{ 
                      color: isMapped ? '#4caf50' : '#f44336',
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}>
                      {fieldLabel}
                    </Typography>
                  </StatusIndicator>
                );
              })}
            </div>
            
            <Content>
              {/* Validation Error Filter */}
              {rowsWithErrors.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ffb74d',
                  borderRadius: '4px',
                }}>
                  <Typography variant="body2" style={{ color: '#e65100', fontWeight: 'bold' }}>
                    {rowsWithErrors.length} row{rowsWithErrors.length !== 1 ? 's' : ''} have validation errors
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showOnlyErrors}
                        onChange={(e) => setShowOnlyErrors(e.target.checked)}
                        color="warning"
                      />
                    }
                    label="Show only error rows"
                    style={{ margin: 0 }}
                  />
                </div>
              )}
              
              {/* Show previewed data */}
              <div style={{ maxHeight: '400px', overflowY: 'auto', width: '100%', position: 'relative' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ 
                    backgroundColor: '#f5f5f5', 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <tr>
                      {csvHeaders?.map((header, index) => {
                        const labeledColumn = labeledColumns.find(col => col.columnName === header);
                        const isMapped = labeledColumn?.columnLabel;
                        
                        const isIgnored = labeledColumn?.discardColumn;
                        
                        return (
                          <th key={index} style={{ 
                            padding: '8px', 
                            textAlign: 'left', 
                            borderBottom: '1px solid #ddd', 
                            fontSize: '12px',
                            border: `2px solid ${isIgnored ? '#8d6e63' : isMapped ? '#4caf50' : '#e0e0e0'}`,
                            backgroundColor: isIgnored ? '#efebe9' : isMapped ? '#f8fff8' : '#f5f5f5'
                          }}>
                            <div style={{ marginBottom: '8px' }}>
                              <Typography variant="subtitle2" style={{ 
                                fontWeight: 'bold',
                                color: isIgnored ? '#5d4037' : isMapped ? '#2e7d32' : '#666',
                                fontSize: '11px'
                              }}>
                                {header}
                              </Typography>
                            </div>
                            <FormControl fullWidth size="small" style={{ minWidth: '120px' }}>
                              <Select
                                value={labeledColumn?.discardColumn ? 'none' : (labeledColumn?.columnLabel || '')}
                                onChange={(e) => handleColumnLabelChange(header, e.target.value || undefined)}
                                displayEmpty
                                size="small"
                                style={{ 
                                  backgroundColor: isIgnored ? '#d7ccc8' : isMapped ? '#e8f5e8' : '#fff',
                                  fontSize: '10px',
                                  height: '32px'
                                }}
                              >
                                <MenuItem value="" style={{ fontSize: '10px' }}>
                                  <em>Select field type</em>
                                </MenuItem>
                                {columnMappingOptions.map((option) => {
                                  // Check if this option is already mapped to another column
                                  const isAlreadyMapped = labeledColumns.some(col => 
                                    col.columnName !== header && 
                                    col.columnLabel === option.value && 
                                    !col.discardColumn
                                  );
                                  
                                  return (
                                    <MenuItem 
                                      key={option.value} 
                                      value={option.value} 
                                      style={{ 
                                        fontSize: '10px',
                                        opacity: isAlreadyMapped ? 0.5 : 1,
                                        color: isAlreadyMapped ? '#999' : 'inherit'
                                      }}
                                      disabled={isAlreadyMapped}
                                    >
                                      {option.label}
                                      {isAlreadyMapped && ' (already mapped)'}
                                    </MenuItem>
                                  );
                                })}
                                <MenuItem value="none" style={{ fontSize: '10px', color: '#d32f2f' }}>
                                  Ignore data
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.slice(0, 40).map((displayRow, rowIndex) => {
                      const sourceRowIndex = cleanedData.indexOf(displayRow);
                      const {
                        rawData,
                        cleanedData: cleanedRowData,
                        validationErrors
                      } = displayRow;

                      return (
                      <tr key={sourceRowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#fafafa' }}>
                        {csvHeaders?.map((header, colIndex) => {
                          const labeledColumn = labeledColumns.find(col => col.columnName === header);
                          
                          // Get the value to display
                          let displayValue = labeledColumn?.columnLabel ? (cleanedRowData?.[labeledColumn.columnLabel]) : rawData?.[header] || '';
                          let valueType = 'string';
                          
                          if (labeledColumn?.columnLabel && cleanedRowData?.[labeledColumn?.columnLabel]) {
                            valueType = typeof displayValue;
                          }

                          const validationError = labeledColumn?.columnLabel ? validationErrors?.[labeledColumn?.columnLabel] : null;
                          if (
                            validationError &&
                            (labeledColumn?.columnLabel === 'party' || labeledColumn?.columnLabel === 'raceParty')
                          ) {
                            displayValue = rawData?.[header] || '';
                          }
                          
                          const cellContent = (
                            <td key={colIndex} style={{ 
                              padding: labeledColumn?.columnLabel ? '8px 32px 8px 8px' : '8px',
                              borderBottom: '1px solid #eee', 
                              fontSize: '12px',
                              color: validationError ? '#d32f2f' : 'inherit',
                              backgroundColor: validationError ? '#ffebee' : 'inherit',
                              border: validationError ? '2px solid #f44336' : 'none',
                              position: 'relative'
                            }}>
                              {labeledColumn?.columnLabel && sourceRowIndex >= 0 && (
                                <IconButton
                                  aria-label={`Edit raw value for ${header}`}
                                  title="Edit raw value"
                                  size="small"
                                  onClick={() => {
                                    setCellEditError(null);
                                    setPendingCellEdit({
                                      rowIndex: sourceRowIndex,
                                      columnName: header,
                                      columnLabel: labeledColumn.columnLabel!,
                                      value: String(rawData?.[header] ?? '')
                                    });
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    padding: '3px',
                                    color: validationError ? '#d32f2f' : '#757575'
                                  }}
                                >
                                  <EditIcon style={{ fontSize: '14px' }} />
                                </IconButton>
                              )}
                              <CellRenderer
                                columnLabel={labeledColumn?.columnLabel}
                                value={displayValue}
                                validationError={validationError}
                                partyOptions={allowedPartyOptions}
                                onPartyRemedy={
                                  validationError &&
                                  labeledColumn?.columnLabel &&
                                  ['party', 'raceParty'].includes(labeledColumn.columnLabel)
                                    ? (party) => handlePartyRemedy({
                                      columnName: header,
                                      rawValue: rawData?.[header] || '',
                                      party,
                                    })
                                    : undefined
                                }
                              />
                            </td>
                          );

                          return validationError ? (
                            <Tooltip 
                              key={colIndex}
                              title={validationError}
                              arrow
                              placement="top"
                            >
                              {cellContent}
                            </Tooltip>
                          ) : cellContent;
                        })}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {displayData.length > 40 && (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px', textAlign: 'center' }}>
                  Showing first 40 rows of {displayData.length} total rows
                  {showOnlyErrors && cleanedData && cleanedData.length !== displayData.length && (
                    <span> (filtered from {cleanedData.length} total rows)</span>
                  )}
                </Typography>
              )}
              
              {/* Submission Button */}
              {isMappingComplete && (
                                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleMappingComplete}
                    style={{ 
                      minWidth: '200px',
                      height: '48px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      alignSelf: 'flex-start'
                    }}
                  >
                    Next
                  </Button>
              )}
            </Content>
          </>
        ) : (
          <>
            <Typography style={{ marginBottom: '24px', marginTop: '36px' }} variant={'h1'}>
              Loading Candidate List...
            </Typography>
          </>
        )}

        <Dialog
          open={Boolean(pendingCellEdit)}
          onClose={handleCloseCellEdit}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit mapped cell</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              margin="dense"
              label={pendingCellEdit ? `${pendingCellEdit.columnName} raw value` : 'Raw value'}
              value={pendingCellEdit?.value ?? ''}
              onChange={(event) => setPendingCellEdit(current => (
                current ? { ...current, value: event.target.value } : current
              ))}
              disabled={isSavingCellEdit}
              helperText="The edited raw value will be cleaned and validated using the column mapping."
            />
            {cellEditError && (
              <Alert severity="error" style={{ marginTop: '12px' }}>
                {cellEditError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCellEdit} disabled={isSavingCellEdit}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveCellEdit}
              disabled={isSavingCellEdit}
            >
              {isSavingCellEdit ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Modal */}
        <Dialog
          open={showConfirmationModal}
          onClose={handleCloseMappingConfirmation}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Confirm Mapping Completion
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
              Identified {getUniqueRacesAndCandidates.uniqueRaces} unique races and {getUniqueRacesAndCandidates.uniqueCandidates} candidates.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please review the data before proceeding to the next step.
            </Typography>
            {mappingConfirmationError && (
              <Alert severity="error" style={{ marginTop: '16px' }}>
                {mappingConfirmationError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMappingConfirmation} disabled={isConfirmingMapping}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleConfirmMapping}
              disabled={isConfirmingMapping}
            >
              {isConfirmingMapping ? 'Confirming...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </Wrapper>
    );
  }

  if(candidateList && displayedStatus === 'grouped') {
    return (
      <Wrapper>
        {renderCandidateListToolbar()}

        <Dialog
          open={showEntityRematchModal}
          onClose={() => {
            if (!isRematchingEntities) {
              setShowEntityRematchModal(false);
              setEntityRematchError(null);
            }
          }}
        >
          <DialogTitle>Re-run entity matching?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This will replace the current office and district labels and clear
              all race and official matches from the Merge with Existing step.
            </Typography>
            {entityRematchError && (
              <Alert severity="error" sx={{ marginTop: 2 }}>
                {entityRematchError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowEntityRematchModal(false);
                setEntityRematchError(null);
              }}
              disabled={isRematchingEntities}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEntityRematch}
              disabled={isRematchingEntities}
            >
              {isRematchingEntities ? 'Re-running...' : 'Re-run matching'}
            </Button>
          </DialogActions>
        </Dialog>
        
        <Content style={{ paddingTop: '24px'}}>
          
          {/* Office and District Labeling Progress */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', gap: '24px', flexDirection: 'column', alignItems: 'stretch', minWidth: '500px'}}>
              {/* Office Labeling Progress */}
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
                    Office Labeling Progress
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {labelingProgress.labeled} of {labelingProgress.total} offices labeled
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={labelingProgress.percentage} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    }
                  }}
                />
              </Box>
              
              {/* District Labeling Progress */}
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
                    District Labeling Progress
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {districtLabelingProgress.labeled} of {districtLabelingProgress.total} districts labeled
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={districtLabelingProgress.percentage} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    }
                  }}
                />
              </Box>
            </div>
            
            {/* Filter Controls */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
              gap: 2
            }}>
              {selectedGroupedDataRows.size > 0 && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    {selectedGroupedDataRows.size} selected
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteSelectedGroupedDataRows}
                    disabled={isDeletingGroupedDataRows}
                  >
                    {isDeletingGroupedDataRows ? 'Deleting...' : 'Delete'}
                  </Button>
                </>
              )}
              <TextField
                inputRef={verifyDataSearchInputRef}
                value={verifyDataSearch}
                onChange={event => setVerifyDataSearch(event.target.value)}
                placeholder="Search race, office, or district"
                size="small"
                inputProps={{ 'aria-label': 'Search Verify Data rows' }}
                sx={{ minWidth: 320 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyNeedsAttention}
                    onChange={(e) => setShowOnlyNeedsAttention(e.target.checked)}
                    color="primary"
                  />
                }
                label="Needs Attention"
                style={{ margin: 0 }}
              />
            </Box>
            {groupedDataDeletionError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {groupedDataDeletionError}
              </Alert>
            )}
          </div>
          
          {/* Auto-labeling Progress Indicator */}
          {candidateList?.processInProgress?.stage === 'labeling' && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              padding: '16px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2
              }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress 
                    size={24} 
                    sx={{ color: '#2196f3' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: '500', flex: 1 }}>
                  Auto-labeling {groupedData?.length || 0} records...
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleCancelMatching}
                  disabled={isCancellingMatching}
                >
                  {isCancellingMatching ? 'Cancelling...' : 'Cancel matching'}
                </Button>
              </Box>
              {matchingCancellationError && (
                <Alert severity="error">
                  {matchingCancellationError}
                </Alert>
              )}
              {/* Elapsed Time Display */}
              {candidateList?.processInProgress?.startedAt && (
                <VerifyDataElapsedTime startedAt={candidateList.processInProgress.startedAt} />
              )}
            </Box>
          )}

          {candidateList?.processInProgress?.stage === 'matching' && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '16px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} sx={{ color: '#2196f3' }} />
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500, flex: 1 }}>
                  Matching {groupedData?.length || 0} races with existing data...
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleCancelMatching}
                  disabled={isCancellingMatching}
                >
                  {isCancellingMatching ? 'Cancelling...' : 'Cancel matching'}
                </Button>
              </Box>
              {matchingCancellationError && (
                <Alert severity="error">
                  {matchingCancellationError}
                </Alert>
              )}
              {candidateList.processInProgress.startedAt && (
                <VerifyDataElapsedTime startedAt={candidateList.processInProgress.startedAt} />
              )}
            </Box>
          )}
          
          {/* Duplicate Issues Summary - Only show when not processing */}
          {duplicateAnalysis.duplicates.length > 0 && candidateList?.processInProgress?.stage !== 'labeling' && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              padding: '16px',
              backgroundColor: '#fff3e0',
              border: '1px solid #ffb74d',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2
              }}>
                <WarningIcon sx={{ color: '#f57c00' }} />
                <Typography variant="body2" sx={{ color: '#e65100', fontWeight: '500' }}>
                  {duplicateAnalysis.needsAttention.length} row{duplicateAnalysis.needsAttention.length !== 1 ? 's' : ''} need attention due to duplicates or conflicts
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ color: '#e65100', fontSize: '12px' }}>
                {duplicateAnalysis.duplicates.map((duplicate, index) => {
                  if (duplicate.reason === 'conflicting-seat-identifiers') {
                    return `• ${duplicate.groups.length} rows have the same office, district, and party. Some have seat identifiers while others don't.`;
                  } else if (duplicate.reason === 'duplicate-rows') {
                    return `• ${duplicate.groups.length} rows have identical office, district, and party combinations.`;
                  }
                  return null;
                }).filter(Boolean).join(' ')}
              </Typography>
            </Box>
          )}
          
          
          {election && filteredGroupedData && filteredGroupedData.length > 0 ? (
            <>
              <div style={{ maxHeight: '600px', overflowY: 'auto', width: '100%', position: 'relative' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ 
                  backgroundColor: '#f5f5f5', 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <tr>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '80px'
                    }}>
                      <Checkbox
                        checked={allVisibleGroupedDataRowsSelected}
                        indeterminate={
                          !allVisibleGroupedDataRowsSelected && someVisibleGroupedDataRowsSelected
                        }
                        inputProps={{ 'aria-label': 'select all visible races' }}
                        onChange={event => {
                          setSelectedGroupedDataRows(previousSelectedRows => {
                            const nextSelectedRows = new Set(previousSelectedRows);
                            verifyDataPageData.rows.forEach(group => {
                              if (event.target.checked) {
                                nextSelectedRows.add(group.groupedDataIndex);
                              } else {
                                nextSelectedRows.delete(group.groupedDataIndex);
                              }
                            });
                            return nextSelectedRows;
                          });
                        }}
                        disabled={isDeletingGroupedDataRows}
                        size="small"
                        sx={{ padding: '4px' }}
                      />
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Race/Contest
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Office
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      District
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Special Election
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Seat Identifier
                    </th>
                    {election?.partisan && (
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'left', 
                        borderBottom: '1px solid #ddd',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        Party
                      </th>
                    )}
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      Max Choices
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      # Candidates
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '50px'
                    }}>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {verifyDataPageData.rows.map((group, index) => (
                    <VerifyDataRow
                      key={`${group.groupedDataIndex}-${getGroupPartyKey(group.groupLabel, group.party)}`}
                      group={group}
                      rowIndex={verifyDataPageData.firstVisibleRow - 1 + index}
                      isExpanded={expandedRows.has(group.groupedDataIndex)}
                      isSelected={selectedGroupedDataRows.has(group.groupedDataIndex)}
                      selectionDisabled={isDeletingGroupedDataRows}
                      duplicateReason={duplicateByGroupLabel.get(group.groupLabel)?.reason}
                      electionState={election.state}
                      electionIsPartisan={Boolean(election.partisan)}
                      allowedPartyOptions={allowedPartyOptions}
                      officeDataLoading={officeData === null}
                      districtDataLoading={
                        districtData === null &&
                        candidateList.processInProgress?.stage === 'labeling'
                      }
                      handlersRef={verifyDataRowHandlersRef}
                    />
                  ))}
                </tbody>
                </table>
              </div>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Showing {verifyDataPageData.firstVisibleRow}–{verifyDataPageData.lastVisibleRow} of {filteredGroupedData.length} races
                  {filteredGroupedData.length !== groupedDataWithLabels.length
                    ? ` (${groupedDataWithLabels.length} total)`
                    : ''}
                </Typography>
                {verifyDataPageData.pageCount > 1 && (
                  <Pagination
                    count={verifyDataPageData.pageCount}
                    page={verifyDataPageData.page}
                    onChange={(_, page) => setVerifyDataPage(page)}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
            </>
          ) : (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1" color="textSecondary">
                {verifyDataSearch.trim()
                  ? 'No races match your search.'
                  : showOnlyNeedsAttention
                  ? 'All offices have been labeled with high confidence! No rows need attention.'
                  : 'No grouped data available. Please ensure the data has been properly processed.'
                }
              </Typography>
            </div>
          )}
          
          {/* Completion Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!(indecesNeedingLabelingAttention.length === 0 && (groupedDataWithLabels?.length ?? 0) > 0)}
              onClick={handleLabelingComplete}
              style={{ 
                minWidth: '200px',
                height: '48px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Complete Labeling
            </Button>
          </div>
        </Content>
      </Wrapper>
    )
  }

  if(candidateList && displayedStatus === 'labeled') {
    return (
      <Wrapper>
        {renderCandidateListToolbar()}
        
        <Content>
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            marginBottom: '24px'
          }}>
            <Typography variant="body1" sx={{ color: '#666' }}>
              Review the data to ensure it will be properly merged with the existing dataset.
            </Typography>
          </Box>

          {matchingRetryError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {matchingRetryError}
            </Alert>
          )}
          
          {/* Auto-matching Progress Indicator */}
          {candidateList?.processInProgress?.stage === 'matching' && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              padding: '16px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2
              }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress 
                    size={24} 
                    sx={{ color: '#2196f3' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: '500', flex: 1 }}>
                  Auto-matching {groupedData?.length || 0} races with existing data...
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleCancelMatching}
                  disabled={isCancellingMatching}
                >
                  {isCancellingMatching ? 'Cancelling...' : 'Cancel'}
                </Button>
              </Box>

              {matchingCancellationError && (
                <Alert severity="error">
                  {matchingCancellationError}
                </Alert>
              )}
              
              {/* Elapsed Time Display */}
              {candidateList?.processInProgress?.startedAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#1976d2' }}>
                    Started: {new Date(candidateList.processInProgress.startedAt).toLocaleTimeString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#1976d2' }}>
                    • Elapsed: {(() => {
                      const startTime = new Date(candidateList.processInProgress.startedAt).getTime();
                      const elapsedMs = currentTime - startTime;
                      const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));
                      const elapsedSeconds = Math.floor((elapsedMs % (1000 * 60)) / 1000);
                      
                      if (elapsedMinutes >= 60) {
                        const elapsedHours = Math.floor(elapsedMinutes / 60);
                        const remainingMinutes = elapsedMinutes % 60;
                        return `${elapsedHours}h ${remainingMinutes}m ${elapsedSeconds}s`;
                      } else {
                        return `${elapsedMinutes}m ${elapsedSeconds}s`;
                      }
                    })()}
                  </Typography>
                </Box>
              )}
              
              {/* Warning Message for Long Running Process */}
              {candidateList?.processInProgress?.startedAt && (() => {
                const startTime = new Date(candidateList.processInProgress.startedAt).getTime();
                const elapsedHours = (currentTime - startTime) / (1000 * 60 * 60);
                
                if (elapsedHours > 1) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      padding: '8px 12px',
                      backgroundColor: '#fff3e0',
                      border: '1px solid #ffb74d',
                      borderRadius: '4px'
                    }}>
                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: '500' }}>
                        ⚠️ This shouldn't be taking this long, contact an admin
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}
            </Box>
          )}
          
          {/* Summary Stats */}
          <div style={{ 
            display: 'flex', 
            gap: '24px', 
            marginBottom: '24px',
            flexWrap: 'wrap'
          }}>
            {/* Race Matching Stats */}
            <div style={{ 
              flex: '1',
              minWidth: '280px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  backgroundColor: '#007bff', 
                  borderRadius: '50%' 
                }} />
                <Typography variant="h6" style={{ fontWeight: '600', color: '#495057' }}>
                  Race Status
                </Typography>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#6c757d' }}>
                    Matched to existing races:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '600', color: '#007bff' }}>
                    {raceStats.matched}
                  </Typography>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#6c757d' }}>
                    Will be created new:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '600', color: '#28a745' }}>
                    {raceStats.new}
                  </Typography>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#6c757d' }}>
                    Unmatched:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '600', color: '#6c757d' }}>
                    {raceStats.total - raceStats.matched - raceStats.new}
                  </Typography>
                </div>
                
                <div style={{ 
                  height: '1px', 
                  backgroundColor: '#dee2e6', 
                  margin: '8px 0'
                }} />
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#495057', fontWeight: '500' }}>
                    Total races:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '700', color: '#495057' }}>
                    {raceStats.total}
                  </Typography>
                </div>
              </div>
            </div>

            {/* Candidate Matching Stats */}
            <div style={{ 
              flex: '1',
              minWidth: '280px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  backgroundColor: '#28a745', 
                  borderRadius: '50%' 
                }} />
                <Typography variant="h6" style={{ fontWeight: '600', color: '#495057' }}>
                  Candidate Status
                </Typography>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#6c757d' }}>
                    Matched to officials:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '600', color: '#28a745' }}>
                    {candidateStats.matched}
                  </Typography>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#6c757d' }}>
                    Will be created new:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '600', color: '#007bff' }}>
                    {candidateStats.new}
                  </Typography>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#6c757d' }}>
                    Unmatched:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '600', color: '#6c757d' }}>
                    {candidateStats.total - candidateStats.matched - candidateStats.new}
                  </Typography>
                </div>
                
                <div style={{ 
                  height: '1px', 
                  backgroundColor: '#dee2e6', 
                  margin: '8px 0'
                }} />
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" style={{ color: '#495057', fontWeight: '500' }}>
                    Total candidates:
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: '700', color: '#495057' }}>
                    {candidateStats.total}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          
          {matchRowDeletionError && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {matchRowDeletionError}
            </Alert>
          )}

          <Dialog
            open={Boolean(pendingMatchRowDeletion)}
            onClose={() => setPendingMatchRowDeletion(null)}
          >
            <DialogTitle>
              {pendingMatchRowDeletion?.type === 'race' ? 'Remove race?' : 'Remove candidate?'}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                {pendingMatchRowDeletion?.type === 'race'
                  ? `Remove ${pendingMatchRowDeletion.label} and all of its candidates from this candidate list?`
                  : `Remove ${pendingMatchRowDeletion?.label} from this candidate list?`}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                This change will be saved immediately.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingMatchRowDeletion(null)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmMatchRowDeletion}
              >
                Remove
              </Button>
            </DialogActions>
          </Dialog>

          {flattenedMatchData && flattenedMatchData.length > 0 ? (
            <div style={{ maxHeight: '600px', overflowY: 'auto', width: '100%', position: 'relative' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ 
                  backgroundColor: '#f5f5f5', 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <tr>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '25%'
                    }}>
                      Race/Contest
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '25%'
                    }}>
                      Race Match
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '30%'
                    }}>
                      Candidate
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '17%'
                    }}>
                      Official Match
                    </th>
                    <th style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      width: '3%'
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {flattenedMatchData.map((row, index) => {
                    const isRaceRow = row.type === 'race';
                    const backgroundColor = row.groupIndex % 2 === 0 ? '#fff' : '#fafafa';
                    const raceRowBackground = row.groupIndex % 2 === 0 ? '#f8f9fa' : '#f1f3f4';
                    
                    return (
                      <tr key={`${row.groupIndex}-${row.type}-${row.candidateIndex || 0}`} style={{ 
                        backgroundColor: isRaceRow ? raceRowBackground : backgroundColor,
                        borderTop: isRaceRow ? '2px solid #e0e0e0' : 'none'
                      }}>
                        <td style={{ 
                          padding: '8px 6px', 
                          borderBottom: '1px solid #eee', 
                          fontSize: '14px',
                          fontWeight: isRaceRow ? 'bold' : 'normal'
                        }}>
                          {isRaceRow ? (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '13px' }}>{row.groupData.groupLabel}</span>
                                {row.groupData.party && (
                                  <PartyTag party={row.groupData.party} preloadedParties={allowedPartyOptions} />
                                )}
                              </div>
                              <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.2' }}>
                                {row.groupData.officeInfo?.name && (
                                  <div style={{ marginBottom: '1px' }}>Office: {row.groupData.officeInfo.name}</div>
                                )}
                                {row.groupData.districtInfo?.name && (
                                  <div>District: {row.groupData.districtInfo.name}</div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                            </div>
                          )}
                        </td>
                        <td style={{ 
                          padding: '8px 6px', 
                          borderBottom: '1px solid #eee', 
                          fontSize: '14px'
                        }}>
                          {(isRaceRow && election) ? (
                            <RaceCell 
                              loading={!row.groupData.matchData} 
                              raceMatch={row.groupData.matchData?.raceMatch ?? null} 
                              raceInfo={row.groupData.matchData?.raceInfo}
                              election={election}
                              groupLabel={row.groupData.groupLabel}
                              onRaceChange={handleRaceChange}
                            />
                          ) : (
                            <div style={{ paddingLeft: '24px', color: '#ccc' }}>
                              —
                            </div>
                          )}
                        </td>
                        <td style={{ 
                          padding: '12px 8px', 
                          borderBottom: '1px solid #eee', 
                          fontSize: '14px'
                        }}>
                          {isRaceRow ? (
                            <div style={{ color: '#ccc' }}>
                              —
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap:'6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <div style={{ fontWeight: '500', fontSize: '12px' }}>
                                {row.candidate?.name || 'Unknown Candidate'}
                              </div>
                              {row.candidate?.party && (
                                <PartyTag party={row.candidate.party} preloadedParties={partyOptions}  />
                              )}
                              {row.candidate?.incumbent && (
                                <span style={{ 
                                  fontSize: '10px', 
                                  color: '#4caf50', 
                                  padding: '1px 4px', 
                                  borderRadius: '8px',
                                  border: '1px solid #4caf50'
                                }}>
                                  Incumbent
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ 
                          padding: '8px 6px', 
                          borderBottom: '1px solid #eee', 
                          fontSize: '14px'
                        }}>
                          {isRaceRow ? (
                            <div style={{ color: '#ccc' }}>
                              —
                            </div>
                          ) : (
                            <OfficialsCell
                              officialInfo={row.groupData.matchData?.officialMatches?.find(match => match.candidateName === row.candidate?.name)?.officialInfo || null}
                              candidate={row.candidate!}
                              officialMatch={row.groupData.matchData?.officialMatches?.find(match => match.candidateName === row.candidate?.name) || null}
                              loading={!row.groupData.matchData?.officialMatches?.find(match => match.candidateName === row.candidate?.name)}
                              onOfficialChange={(officialMatch: { official?: Official; create: boolean } | null) => handleOfficialChange(row.groupData.groupLabel, row.candidate?.name || '', officialMatch)}
                              isDuplicate={duplicateOfficialMatches.needsAttention.has(`${row.groupData.groupLabel}${row.groupData.party ? `-${row.groupData.party}` : ''}-${row.candidate?.name || ''}`)}
                            />
                          )}
                        </td>
                        <td style={{
                          padding: '8px 6px',
                          borderBottom: '1px solid #eee',
                          textAlign: 'center'
                        }}>
                          <Tooltip
                            title={isRaceRow ? 'Remove race and all its candidates' : 'Remove candidate'}
                            arrow
                            placement="top"
                          >
                            <span>
                              <IconButton
                                size="small"
                                sx={{ color: '#666' }}
                                disabled={
                                  Boolean(deletingMatchRow) ||
                                  candidateList?.processInProgress?.stage === 'matching'
                                }
                                onClick={() => {
                                  if (isRaceRow) {
                                    setPendingMatchRowDeletion({
                                      type: 'race',
                                      groupIndex: row.groupIndex,
                                      label: row.groupData.groupLabel
                                    });
                                  } else if (typeof row.candidateIndex === 'number') {
                                    setPendingMatchRowDeletion({
                                      type: 'candidate',
                                      groupIndex: row.groupIndex,
                                      candidateIndex: row.candidateIndex,
                                      label: row.candidate?.name || 'this candidate'
                                    });
                                  }
                                }}
                                aria-label={isRaceRow ? `Remove race ${row.groupData.groupLabel}` : `Remove candidate ${row.candidate?.name}`}
                              >
                                {deletingMatchRow === (
                                  isRaceRow
                                    ? `race-${getGroupPartyKey(row.groupData.groupLabel, row.groupData.party)}`
                                    : `candidate-${getGroupPartyKey(row.groupData.groupLabel, row.groupData.party)}-${row.candidateIndex}`
                                )
                                  ? <CircularProgress size={16} />
                                  : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1" color="textSecondary">
                No matched data available. Please ensure the data has been properly processed.
              </Typography>
            </div>
          )}
          
          {/* Warning for duplicate official matches */}
          {duplicateOfficialMatches.duplicates.length > 0 && (
            <div style={{ 
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <WarningIcon style={{ color: '#856404', fontSize: '24px', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <Typography variant="body1" style={{ fontWeight: '600', color: '#856404', marginBottom: '8px' }}>
                  Duplicate Official Matches Detected
                </Typography>
                <Typography variant="body2" style={{ color: '#856404', marginBottom: '12px' }}>
                  The following officials are matched to multiple candidates. Please fix these matches before proceeding:
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {duplicateOfficialMatches.duplicates.map((duplicate, index) => {
                    const officialInfo = officialData?.find(o => o.key === duplicate.officialKey);
                    return (
                      <div key={index} style={{ 
                        padding: '8px 12px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        border: '1px solid #ffc107'
                      }}>
                        <Typography variant="body2" style={{ fontWeight: '600', color: '#856404', marginBottom: '4px' }}>
                          {officialInfo?.name || duplicate.officialKey}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#856404', fontSize: '12px' }}>
                          Matched to: {duplicate.candidates.map(c => `${c.candidateName} (${c.groupLabel}${c.party ? ` - ${c.party}` : ''})`).join(', ')}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* Completion Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={
                !flattenedMatchData ||
                flattenedMatchData.length === 0 ||
                duplicateOfficialMatches.duplicates.length > 0 ||
                candidateList?.processInProgress?.stage === 'matching' ||
                isRetryingMatching ||
                Boolean(deletingMatchRow)
              }
              onClick={() => {
                markDataAsMatched();
              }}
              style={{ 
                minWidth: '200px',
                height: '48px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Next
            </Button>
          </div>
        </Content>
      </Wrapper>
    );
  }

  if (candidateList && displayedStatus === 'matched') {
    return (
      <Wrapper>
        {renderCandidateListToolbar()}
        <Content style={{ maxWidth: '600px'}}>
          <ScoutingStepContent
            candidateList={candidateList}
            election={election}
            electionKey={electionKey!}
            feathers={feathers}
            onConfirmScouting={confirmScouting}
            onPatchCandidateList={patchCandidateList}
          />
        </Content>
      </Wrapper>
    );
  }

  if (candidateList && displayedStatus === 'scouted') {
    return (
      <Wrapper>
        {renderCandidateListToolbar()}
        
        <Content>
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            marginBottom: '24px'
          }}>
            <Typography variant="body1" sx={{ color: '#666' }}>
              Review the operations that will be performed when creating this candidate list. Once confirmed, the system will create all necessary entities.
            </Typography>
            {!hasFinishedDataOperations && (
              <Tooltip title="Regenerate data operations from the latest server data">
                <span>
                  <IconButton
                    aria-label="Regenerate data operations"
                    onClick={handleRegenerateDataOperations}
                    disabled={isCreatingDataOperations || isRegeneratingDataOperations}
                    size="small"
                  >
                    {isRegeneratingDataOperations
                      ? <CircularProgress size={20} />
                      : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {regenerateDataOperationsError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {regenerateDataOperationsError}
            </Alert>
          )}
          
          {/* Creation Progress Indicator */}
          {candidateList?.processInProgress?.stage === 'creating' && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              padding: '16px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2
              }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress 
                    size={24} 
                    sx={{ color: '#2196f3' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: '500' }}>
                  Creating entities from {dataOperations?.length || 0} operations...
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleCancelCreateEntities}
                  disabled={isCancellingCreate}
                  style={{ marginLeft: 'auto' }}
                >
                  {isCancellingCreate ? 'Cancelling...' : 'Cancel'}
                </Button>
              </Box>
              
              {/* Progress Bar */}
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
                    Progress: {dataOperations?.filter(op => op.status === 'completed' || op.status === 'no-op').length || 0} of {dataOperations?.length || 0} operations completed
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {dataOperations && dataOperations.length > 0 ? 
                      Math.round((dataOperations.filter(op => op.status === 'completed' || op.status === 'no-op').length / dataOperations.length) * 100) : 0
                    }%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={dataOperations && dataOperations.length > 0 ? 
                    (dataOperations.filter(op => op.status === 'completed' || op.status === 'no-op').length / dataOperations.length) * 100 : 0
                  } 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    }
                  }}
                />
              </Box>
              
              {/* Elapsed Time Display */}
              {candidateList?.processInProgress?.startedAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#1976d2' }}>
                    Started: {new Date(candidateList.processInProgress.startedAt).toLocaleTimeString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#1976d2' }}>
                    • Elapsed: {(() => {
                      const startTime = new Date(candidateList.processInProgress.startedAt).getTime();
                      const elapsedMs = currentTime - startTime;
                      const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));
                      const elapsedSeconds = Math.floor((elapsedMs % (1000 * 60)) / 1000);
                      
                      if (elapsedMinutes >= 60) {
                        const elapsedHours = Math.floor(elapsedMinutes / 60);
                        const remainingMinutes = elapsedMinutes % 60;
                        return `${elapsedHours}h ${remainingMinutes}m ${elapsedSeconds}s`;
                      } else {
                        return `${elapsedMinutes}m ${elapsedSeconds}s`;
                      }
                    })()}
                  </Typography>
                </Box>
              )}
              
              {/* Warning Message for Long Running Process */}
              {candidateList?.processInProgress?.startedAt && (() => {
                const startTime = new Date(candidateList.processInProgress.startedAt).getTime();
                const elapsedHours = (currentTime - startTime) / (1000 * 60 * 60);
                
                if (elapsedHours > 1) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      padding: '8px 12px',
                      backgroundColor: '#fff3e0',
                      border: '1px solid #ffb74d',
                      borderRadius: '4px'
                    }}>
                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: '500' }}>
                        ⚠️ This shouldn't be taking this long, contact an admin
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}
            </Box>
          )}
          
          {/* Data Operations Queue */}
          {dataOperationsErrorAlert}
          <DataOperationsTable 
            dataOperations={dataOperations}
            title="Data Operations Queue"
            defaultToOperationsTab={shouldDefaultToOperationsTab}
            onChangeDataOperations={saveDataOperations}
            onChangeOfficialMatch={canChangeOfficialMatches ? changeOfficialMatch : undefined}
            electionKey={electionKey}
            onRetryFailedOperations={
              canRetryDataOperations
                ? handleCreateEntities
                : undefined
            }
          />
          
          {/* Confirmation Button */}
          {!canRetryDataOperations && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleCreateEntities}
                style={{ 
                  minWidth: '200px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
                disabled={candidateList?.processInProgress?.stage === 'creating'}
              >
                Create
              </Button>
            </div>
          )}
        </Content>
      </Wrapper>
    );
  }



  if(candidateList && displayedStatus === 'created') {
    const completedOperationCount = dataOperations?.filter(
      operation => operation.status === 'completed' || operation.status === 'no-op'
    ).length || 0;
    const operationCount = dataOperations?.length || 0;
    const creationPercentage = operationCount > 0
      ? (completedOperationCount / operationCount) * 100
      : 0;

    return (
      <Wrapper>
        {renderCandidateListToolbar()}
        
                  <Content>
            {candidateList.processInProgress?.stage === 'creating' && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                padding: '16px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #2196f3',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} sx={{ color: '#2196f3' }} />
                  <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: '500' }}>
                    Retrying failed operations...
                  </Typography>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Progress: {completedOperationCount} of {operationCount} operations completed
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={creationPercentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { borderRadius: 4 }
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Error Title for Failed Operations */}
            {dataOperations && dataOperations.some(op => op.status === 'failed') && (
              <Typography variant="h2" style={{ marginBottom: '16px', color: '#d32f2f' }}>
                ⚠️ Creation Completed with Errors
              </Typography>
            )}
            
            {/* Success Title for All Operations Successful */}
            {dataOperations && !dataOperations.some(op => op.status === 'failed') && (
              <Typography variant="h2" style={{ marginBottom: '16px', color: '#2e7d32' }}>
                ✓ Creation Complete!
              </Typography>
            )}
            
            <Typography variant="body1" style={{ marginBottom: '24px', color: '#666' }}>
              Your candidate list has been successfully created. You will be redirected to the election page shortly.
            </Typography>
            
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              backgroundColor: dataOperations && dataOperations.some(op => op.status === 'failed') ? '#ffebee' : '#e8f5e8', 
              borderRadius: '4px',
              border: `1px solid ${dataOperations && dataOperations.some(op => op.status === 'failed') ? '#f44336' : '#4caf50'}`
            }}>
              <Typography variant="body1" style={{ 
                fontWeight: 'bold',
                color: dataOperations && dataOperations.some(op => op.status === 'failed') ? '#d32f2f' : '#2e7d32'
              }}>
                {dataOperations && dataOperations.some(op => op.status === 'failed') 
                  ? '⚠️ Candidate list creation completed with some errors' 
                  : '✓ Candidate list creation completed successfully'
                }
              </Typography>
            </div>
            
            {/* Final Data Operations Summary */}
            {dataOperationsErrorAlert}
            <DataOperationsTable
              dataOperations={dataOperations}
              title="Final Operation Results"
              defaultToOperationsTab={shouldDefaultToOperationsTab}
              onChangeDataOperations={saveDataOperations}
              onChangeOfficialMatch={canChangeOfficialMatches ? changeOfficialMatch : undefined}
              electionKey={electionKey}
              onRetryFailedOperations={
                canRetryDataOperations
                  ? handleCreateEntities
                  : undefined
              }
            />
        </Content>
      </Wrapper>
    );
  }

  return null;
};

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  min-height: calc(100% - 24px * 2);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CSVUploadTile = styled(Tile)`
  max-width: 600px;
  margin: 0 auto;
`;


const DataPreviewTile = styled(Tile)`
  max-width: 100%;
  margin: 0 auto;
`;



const StatusIndicator = styled.div<{ isMapped: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: ${props => props.isMapped ? '#e8f5e8' : '#ffebee'};
  border: 1px solid ${props => props.isMapped ? '#c8e6c9' : '#ffcdd2'};
  border-radius: 6px;
  transition: all 0.2s ease;
`;

export default UploadCandidateList;
